// src/engines/EngineMode.jsx
// ══════════════════════════════════════════════════════════════════════════════
// ENGINE MODE — Conecta el sistema de 9 motores con el UI de AdventureMode
//
// Arquitectura:
//   1. EngineSetup   → usuario define contexto + objetivo
//   2. BlockOverview → mapa visual de bloques + progreso
//   3. BlockLoading  → EngineLoader (IA genera grammar note + 5 ejercicios)
//   4. BlockGame     → juega el bloque (grammar note → 5 ejercicios)
//   5. BlockResult   → score del bloque → guarda progreso
//   6. ClassComplete → celebración final con resumen
//
// API Endpoints:
//   GET  /api/engines/motor-info/{lesson_id}  → info del motor + progreso
//   POST /api/engines/generate-block          → genera un bloque con IA
//   POST /api/engines/save-progress           → guarda score del bloque
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from "react";
import { LIMITES } from '../limites';
import EngineLoader from "./EngineLoader";
import { ReadAloudCard, SpeakScoreCard, ShadowingCard, MinimalPairCard, PronunciationScoredCard } from "./VoiceLabCards.jsx";
// La Arena vive fuera de `engines/` a propósito: es un motor de razonamiento
// aparte, con su propio backend, y no comparte una línea con el resto del curso.
// Esta es la ÚNICA costura entre los dos mundos.
import ArenaBlock from "../arena/ArenaBlock.jsx";
import { Confetti } from "./EngineBanner.jsx";
// ── EL SONIDO DEL CURSO ──────────────────────────────────────────────────────
// Estos sonidos existian solo dentro del Dojo. Eran el unico sitio del producto
// que sonaba, y tambien el unico que se sentia vivo. Aqui se le dan al camino
// principal, que es donde el alumno pasa el 90% de su tiempo.
//
// No suena cada clic: suenan los momentos que SIGNIFICAN algo —aciertas,
// fallas, empiezas, cierras— y las acciones que avanzan de pantalla. Lo demas
// calla. Un sonido en cada toque deja de ser informacion en diez minutos.
import {
    fxHit, fxCombo, fxMiss, fxStart, fxFinish, fxRecord, fxAvanzar,
} from "../audio/fx";
import "./EngineMode.css";
// Se importa por el confeti: la pieza y su animacion viven en esta hoja. Todas
// sus clases llevan prefijo `ebn-`, asi que no pisa nada de EngineMode.
import "./EngineBanner.css";

/* ══════════════════════════════════════════════════════════════════════════════
   SPACY ES UN PASO DEL CAMINO, NO OTRO MUNDO

   Antes, para conversar había que salirse del Modo IA e irse al Modo Clásico.
   Ahora la conversación aparece como un paso más del mapa de la lección.

   ⚠️  ESTO ES UNA MUDANZA, NO UNA REESCRITURA.
   El componente que se usa es el MISMO `SpeakingExercise` del Modo Clásico, sin
   una línea modificada: conserva su modo cinema, su micrófono, sus botones de
   pánico, su guard de tokens, sus GIFs y toda su calibración.

   Y por eso NO se monta dentro de este archivo. El contenedor `.engine-mode`
   impone `overflow-x: hidden` en el área de scroll y
   `button { white-space: nowrap; overflow: hidden }` a TODOS sus botones —
   dos reglas que le romperían el layout al entrar. EngineMode sólo avisa
   («el alumno quiere hablar») y LessonPage lo renderiza en el mismo sitio y
   con las mismas props que tenía en el Modo Clásico.

   Para moverlo al cierre en vez de a la apertura basta cambiar esta constante
   a "close" — el resto del componente ya lo respeta.
   ══════════════════════════════════════════════════════════════════════════ */
const SPACY_POSITION = "open";   // "open" | "close"

/* ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
   Todo sale de las variables `--em-*` que EngineMode.css define para los DOS
   temas. Antes estos valores estaban escritos a mano y eran de modo oscuro:
   `faint` era blanco al 45 %, invisible sobre un fondo claro, y `green`/`red`
   eran neones pensados para fondo negro.

   El modo oscuro no cambia ni un color: los tokens repiten exactamente lo que
   había. Lo que se añade es que ahora también existe el claro.

   Ojo con el amarillo: `accent` es RELLENO (lleva tinta negra encima) y
   `accentInk` es TEXTO. En claro no pueden ser el mismo color — amarillo puro
   sobre gris claro no se lee.
   ──────────────────────────────────────────────────────────────────────────── */
const C = {
    bg: "var(--bg-primary)",
    bgCard: "var(--bg-secondary)",
    border: "var(--em-hairline)",
    accent: "var(--em-accent-ink)",       // texto en color de acento
    accentFill: "var(--em-accent)",       // relleno amarillo, con texto negro
    accentDim: "var(--em-accent-dim)",
    accentBorder: "var(--em-accent-border)",
    green: "var(--em-green)",
    greenDim: "var(--em-green-dim)",
    red: "var(--em-red)",
    redDim: "var(--em-red-dim)",
    blue: "var(--em-blue)",
    blueDim: "var(--em-blue-dim)",
    purple: "var(--em-purple)",
    purpleDim: "var(--em-purple-dim)",
    purpleBorder: "var(--em-purple-border)",
    orange: "var(--em-orange)",
    orangeDim: "var(--em-orange-dim)",
    text: "var(--em-text)",
    muted: "var(--em-muted)",
    faint: "var(--em-faint)",
    veil03: "var(--em-veil-03)",
    veil06: "var(--em-veil-06)",
    veil08: "var(--em-veil-08)",
    veil25: "var(--em-veil-25)",
};

const BLOCK_ICONS = [
    "🎯", "✅", "❌", "❓", "🔤", "💬", "🔗", "🏷️", "⚠️", "🔀"
];

const getToken = () => localStorage.getItem("token") || "";
const getSessionKey = (lessonId, blockNum) => `em_session_${lessonId}_${blockNum}`;

/* ── La configuración de la clase se recuerda ──────────────────────────────
   Contexto, objetivo y nivel se preguntan UNA vez por lección. Antes vivían
   sólo en memoria: cualquier desmontaje —volver del temario curado, recargar
   la página— hacía que el motor renaciera en la pantalla de configuración y
   el alumno tuviera que elegirlo todo otra vez.

   Se guarda en sessionStorage y no en localStorage a propósito: es la
   configuración de ESTA sesión de estudio, no una preferencia permanente. */
/* ══════════════════════════════════════════════════════════════════════════════
   EL PROGRESO TIENE DOS FORMAS Y HAY QUE ELEGIR UNA
   ══════════════════════════════════════════════════════════════════════════════
   Durante la sesión, al terminar un bloque se guarda un OBJETO:
       completedBlocks[3] = { score: 8, block_name: "negative" }

   Pero al recargar, el progreso llega del servidor como un NÚMERO:
       student_progress = { "3": 8 }

   La pantalla lee `prog?.score`. Con el objeto funciona; con el número da
   `undefined`, y por eso el alumno veía sus bloques marcados en verde pero con
   el marcador vacío: "/10 puntos" sin número delante. El resultado SÍ estaba
   guardado — solo que se leía de una forma que ya no existía.

   Y había un segundo fallo escondido en la misma lectura: `isDone = !!prog`.
   Un bloque con nota CERO es un 0, que es falso en JavaScript, así que tras
   recargar aparecería como no hecho y el alumno tendría que repetirlo.

   Se normaliza aquí, en la frontera por donde entra la forma ajena. Dentro de
   la aplicación hay UNA forma, y la pantalla no tiene que saber de dónde vino.
   ══════════════════════════════════════════════════════════════════════════════ */
const normalizarProgreso = (crudo) => {
    const salida = {};
    for (const [clave, valor] of Object.entries(crudo || {})) {
        if (valor === null || valor === undefined) continue;
        salida[clave] = (typeof valor === "object") ? valor : { score: valor };
    }
    return salida;
};

const getConfigKey = (lessonId) => `em_cfg_${lessonId}`;

const readConfig = (lessonId) => {
    try { return JSON.parse(sessionStorage.getItem(getConfigKey(lessonId)) || "null"); }
    catch { return null; }
};

/* ─── UTILITIES ───────────────────────────────────────────────────────────── */
/* ─── Marca de énfasis ─────────────────────────────────────────────────────────
   Mismo tratamiento que Spacy (`.spr-bold` en spacy-renderer.css): amarillo
   pleno con tinta oscura. Antes esto era `color: C.accent`, que en oscuro daba
   un texto amarillo y en claro no daba nada — el énfasis desaparecía justo en
   el sitio donde el alumno tiene que fijarse.

   Al ser fondo + tinta propia, se lee igual en los dos temas. */
const Mark = ({ children }) => (
    <mark style={{
        background: "var(--em-mark-bg)",
        color: "var(--em-mark-ink)",
        fontWeight: 700,
        fontStyle: "normal",
        padding: "0.08em 0.35em",
        borderRadius: "5px",
        boxDecorationBreak: "clone",
        WebkitBoxDecorationBreak: "clone",
        margin: "0 0.08em",
    }}>{children}</mark>
);

/* Resalta las **negritas** y los 'ejemplos entrecomillados', que es donde vive
   lo que hay que retener. El cierre de comilla no puede ir pegado a una letra:
   si no, "don't" se partiría por la mitad. */
const EMPHASIS = /\*\*(.+?)\*\*|'([^']{2,80}?)'(?![\w])/g;

/* La IA devuelve la nota gramatical en un solo bloque continuo:
   «…followed by the subject. Structure: Aux + Subject + main verb? Example:
   'Do you study English?' Common error: 'You do study?' Correct: …»

   Sin cortes, el alumno lee un muro. Aquí se parte por las etiquetas que la
   propia nota usa para separar sus partes, de modo que cada idea cae en su
   propio párrafo. Si la nota ya trae saltos de línea, se respetan. */
const NOTE_LABELS = /\s*(Structure|Estructura|Formula|Fórmula|Example|Examples|Ejemplo|Ejemplos|Common error|Common errors|Error común|Error comun|Errores comunes|Correct|Correcto|Incorrect|Incorrecto|Tip|Nota|Note|Remember|Recuerda)\s*:/gi;

const splitNote = (text) =>
    String(text || "")
        .replace(NOTE_LABELS, "\n$1:")
        .split("\n")
        .map(l => l.trim())
        .filter(Boolean);

const parseBold = (text) => {
    if (!text) return null;
    const src = String(text);
    const out = [];
    let last = 0;
    let m;
    EMPHASIS.lastIndex = 0;
    while ((m = EMPHASIS.exec(src)) !== null) {
        if (m.index > last) out.push(src.slice(last, m.index));
        out.push(<Mark key={m.index}>{m[1] ?? m[2]}</Mark>);
        last = m.index + m[0].length;
    }
    if (last < src.length) out.push(src.slice(last));
    return out;
};

/* ─── alpha() ──────────────────────────────────────────────────────────────────
   Antes la opacidad se pegaba al color en hexadecimal: `${C.red}44`. Eso sólo
   funciona si el color ES un hexadecimal literal — en cuanto pasó a ser una
   variable CSS, `var(--em-red)44` dejó de ser un color válido y el navegador
   descartaba la regla en silencio (bordes y resplandores que no se pintaban).

   `color-mix` hace lo mismo pero sobre cualquier color, incluida una variable
   que cambia con el tema. */
const alpha = (color, pct) => `color-mix(in srgb, ${color} ${pct}%, transparent)`;

/* ─── Neumorphic button helper ────────────────────────────────────────────── */
const btnStyle = (bg, fg, disabled = false, extra = {}) => ({
    background: disabled ? "var(--bg-secondary)" : bg,
    color: disabled ? "var(--text-secondary)" : fg,
    border: "none",
    borderRadius: "14px",
    padding: "14px 24px",
    fontSize: "14px",
    fontWeight: 800,
    cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: "inherit",
    transition: "all 0.2s",
    letterSpacing: "0.3px",
    boxShadow: disabled
        ? "inset -3px 3px 6px var(--shadow-dark), inset 3px -3px 6px var(--shadow-light)"
        : "-5px 5px 10px var(--shadow-dark), 5px -5px 10px var(--shadow-light)",
    ...extra,
});

/* ─── Neumorphic card helper ──────────────────────────────────────────────── */
const cardNeu = (extra = {}) => ({
    background: "var(--bg-primary)",
    borderRadius: "20px",
    boxShadow: "-10px 10px 20px var(--shadow-dark), 10px -10px 20px var(--shadow-light)",
    ...extra,
});

/* ─── Neumorphic inset helper ─────────────────────────────────────────────── */
const insetNeu = (extra = {}) => ({
    background: "var(--bg-secondary)",
    borderRadius: "13px",
    boxShadow: "inset -4px 4px 8px var(--shadow-dark), inset 4px -4px 8px var(--shadow-light)",
    ...extra,
});

/* ─── HUD COMPONENTS ──────────────────────────────────────────────────────── */
function FireBar({ energy, shaking }) {
    const pct = Math.max(0, Math.min(100, energy));
    // El degradado y el resplandor viven en CSS porque cambian con el tema:
    // en oscuro es el fuego de siempre; en claro, amarillo plano y sin brillo
    // (el naranja encendido sobre gris claro se veía ajeno a la página).
    const grad = pct > 66 ? "var(--em-fire-full)" :
        pct > 33 ? "var(--em-fire-mid)" :
            "var(--em-fire-low)";
    const glow = pct > 66 ? "var(--em-fire-glow-full)" :
        pct > 33 ? "var(--em-fire-glow-mid)" : "var(--em-fire-glow-low)";
    return (
        <div style={{ display: "flex", alignItems: "center", gap: "7px", flex: 1, animation: shaking ? "fireShake 0.35s ease" : "none" }}>
            <span style={{ fontSize: "19px", animation: pct > 50 ? "fireFlicker 0.45s ease-in-out infinite alternate" : "none" }}>🔥</span>
            <div style={{ flex: 1, position: "relative" }}>
                <div style={{ height: "11px", borderRadius: "6px", overflow: "visible", position: "relative", ...insetNeu({ padding: 0 }) }}>
                    <div style={{
                        width: `${pct}%`, height: "100%", background: grad, borderRadius: "6px",
                        transition: "width 0.65s cubic-bezier(0.34,1.56,0.64,1)",
                        boxShadow: pct > 0 ? `0 0 ${6 + pct / 8}px ${glow},inset 0 1px 0 rgba(255,255,255,0.25)` : "none"
                    }} />
                </div>
            </div>
            <span className="em-firebar-label" style={{
                fontSize: "10px", fontWeight: 800, minWidth: "28px", textAlign: "right",
                color: pct > 66 ? C.accent : pct > 33 ? "var(--em-fire-warn)" : "var(--em-fire-cold)"
            }}>{pct}%</span>
        </div>
    );
}

function XPBar({ xp, level }) {
    const pct = Math.min((xp / 100) * 100, 100);
    return (
        <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
            <span className="em-xp-label" style={{ fontSize: "clamp(10px, 1.2vw, 13px)", fontWeight: 700, color: C.accent, whiteSpace: "nowrap" }}>LV.{level}</span>
            <div style={{ width: "60px", height: "6px", borderRadius: "3px", overflow: "hidden", ...insetNeu({ padding: 0 }) }}>
                <div style={{
                    width: `${pct}%`, height: "100%", background: `linear-gradient(90deg,${C.accentFill},var(--em-fire-warn))`,
                    borderRadius: "3px", transition: "width 0.7s cubic-bezier(0.34,1.56,0.64,1)"
                }} />
            </div>
        </div>
    );
}

function TokenBadge({ remaining, limit }) {
    if (!limit) return null;                          // plan sin límite de tokens → no mostrar
    const pct = limit > 0 ? (remaining / limit) * 100 : 0;
    const color = pct > 40 ? C.green : pct > 15 ? "var(--em-fire-warn)" : C.red;
    const label = remaining > 999 ? `${(remaining / 1000).toFixed(1)}k` : String(remaining);
    return (
        <div style={{
            display: "flex", alignItems: "center", gap: "5px",
            background: "var(--bg-secondary)", borderRadius: "10px",
            padding: "4px 10px",
            boxShadow: pct <= 15
                ? `inset -2px 2px 4px var(--shadow-dark), inset 2px -2px 4px var(--shadow-light), 0 0 8px ${alpha(C.red, 27)}`
                : "inset -2px 2px 4px var(--shadow-dark), inset 2px -2px 4px var(--shadow-light)",
        }}>
            <span style={{ fontSize: "clamp(11px, 1.2vw, 14px)" }}>{pct <= 15 ? "⚠️" : "⚡"}</span>
            <span style={{ fontSize: "clamp(10px, 1.2vw, 13px)", fontWeight: 800, color }}>{label}</span>
            <span style={{ fontSize: "clamp(8px, 1vw, 11px)", color: C.faint }}>tokens</span>
        </div>
    );
}

function ExerciseProgressDots({ total, current, done }) {
    return (
        <div className="em-progress-dots" style={{ display: "flex", gap: "5px", alignItems: "center", justifyContent: "center" }}>
            {Array.from({ length: total }, (_, i) => (
                <div key={i} style={{
                    width: i === current ? "20px" : "7px", height: "7px", borderRadius: "4px",
                    background: i < done ? C.accent : i === current ? C.accentBorder : C.veil08,
                    transition: "all 0.35s cubic-bezier(0.34,1.56,0.64,1)"
                }} />
            ))}
        </div>
    );
}

/* ══════════════════════════════════════════════════════════════════════════════
   EXERCISE CARDS
   ══════════════════════════════════════════════════════════════════════════════ */

/* ══════════════════════════════════════════════════════════════════════════════
   EL IDIOMA DE LA INTERFAZ — español solo en A1
   ══════════════════════════════════════════════════════════════════════════════
   Regla del curso: a partir de A2, TODO en inglés. Cada rótulo en español que
   ve un alumno de B2 es una inmersión que se le quita, y son gratis: "Choose
   the correct one" lo entiende cualquiera que haya pasado A1.

   A1 se queda en español a propósito. Ahí el problema no es la inmersión: es
   que sin entender la instrucción el alumno no hace el ejercicio y se va.

   El contenido (frases, opciones, respuestas) SIEMPRE estuvo en inglés. Esto
   es el andamio de alrededor.
   ══════════════════════════════════════════════════════════════════════════════ */
const TEXTOS = {
    quiz:        ["Elige la correcta", "Choose the correct one"],
    fillBlank:   ["Completa la oración", "Complete the sentence"],
    wordOrder:   ["Ordena las palabras", "Put the words in order"],
    errorDetect: ["¿Correcta o Incorrecta?", "Correct or incorrect?"],
    matching:    ["Une los pares", "Match the pairs"],
    listening:   ["🎧 Escucha y responde", "🎧 Listen and answer"],
    speaking:    ["🗣️ Pronuncia en voz alta", "🗣️ Say it out loud"],
    ejercicio:   ["Ejercicio", "Exercise"],
    de:          ["de", "of"],
    regenerar:   ["⚠️ ¿Error en el ejercicio? Regenerar",
                  "⚠️ Something wrong? Generate a new one"],
    regenerando: ["⏳ Regenerando con IA...", "⏳ Generating a new one..."],

    // ── La tarjeta de escritura ──
    writing:     ["✍️ Escríbelo en inglés", "✍️ Write it in English"],
    enviar:      ["Enviar respuesta ✓", "Send answer ✓"],

    // ── El mapa de la lección ──
    pasosHechos: ["pasos completados", "steps completed"],
    pasos:       ["Pasos", "Steps"],
    completados: ["Completados", "Completed"],
    promedio:    ["Promedio", "Average"],
    empezar:     ["Comenzar primer bloque", "Start the first block"],
    continuar:   ["Continuar:", "Continue:"],
    puntos:      ["puntos", "points"],
    spacyPasos:  ["hablar · escuchar · corregir", "speak · listen · correct"],

    // ── El cierre de bloque ──
    bloqueHecho: ["Bloque Completado", "Block Complete"],
    PUNTOS:      ["PUNTOS", "POINTS"],
    verMapa:     ["Ver mapa →", "See the map →"],
    siguiente:   ["Siguiente bloque ⚡", "Next block ⚡"],
    // La Arena no lleva nota: su veredicto ya se lo dieron al terminar la
    // mision, y era uno de verdad —si resolvio la situacion, que error repetir—
    // no un porcentaje.
    cierreArena: ["Te enfrentaste a la Arena. Tu veredicto ya lo tienes arriba.",
                  "You faced the Arena. Your verdict is above."],
    cierreAlto:  ["¡Excelente dominio! Estás listo para el siguiente bloque. 🔥",
                  "Excellent. You are ready for the next block. 🔥"],
    cierreMedio: ["Buen trabajo. Puedes continuar o repasar este bloque. 💪",
                  "Good work. You can continue or review this block. 💪"],
    // ── Cómo se nombra el modo ──────────────────────────────────────────────
    // ⚠️ NUNCA se nombra al proveedor del modelo.
    // Antes decía "Motor parts_of_speech · DeepSeek AI". Dos problemas: al
    // alumno no le dice nada un identificador interno, y nombrar al proveedor
    // genera desconfianza sin darle nada a cambio. Quien paga una clase quiere
    // saber que la clase es buena, no de quién es el motor que la mueve.
    //
    // Y si mañana se cambia de modelo —ya pasó con la voz— no hay que tocar
    // ninguna pantalla.
    modoIA:      ["Modo IA activo", "AI mode active"],
    motorIA:     ["Modo IA", "AI mode"],
    temario:     ["Temario NSL", "NSL syllabus"],
    bloque:      ["Bloque", "Block"],

    cierreBajo:  ["Este bloque necesita más práctica. Considera repetirlo antes de continuar. 📖",
                  "This block needs more practice. Consider repeating it before you continue. 📖"],
};

/* A1 —y cualquier nivel que no se reconozca— recibe español. Si el nivel llega
   vacío o roto, el alumno entiende la pantalla: es el fallo seguro. */
const esA1 = (nivel) => !nivel || String(nivel).toUpperCase().startsWith("A1");
const T = (nivel, clave) => (TEXTOS[clave] || ["", ""])[esA1(nivel) ? 0 : 1];

/* ─── GRAMMAR NOTE ────────────────────────────────────────────────────────── */
function EngineGrammarCard({ grammarNote, blockLabel, onNext }) {
    // Sin nota no hay tarjeta. `faseVisible` ya evita llegar aquí con un null,
    // pero esto es lo que impide que un bloque sin nota vuelva a dejar al alumno
    // mirando una pantalla en blanco: quien llame mal se salta la tarjeta, no
    // tumba la lección.
    if (!grammarNote) return null;
    return (
        <div className="em-grammar-card" style={{ textAlign: "center" }}>
            <div style={{ fontSize: "42px", marginBottom: "10px" }}>📖</div>
            <div style={{
                fontSize: "9px", fontWeight: 700, color: C.purple, letterSpacing: "2px",
                textTransform: "uppercase", marginBottom: "6px"
            }}>{blockLabel}</div>
            <h2 style={{ fontSize: "18px", fontWeight: 900, color: C.text, marginBottom: "16px", lineHeight: 1.3 }}>
                Nota Gramatical
            </h2>

            {grammarNote.structure_formula && (
                <div className="em-grammar-formula" style={{
                    ...insetNeu(),
                    border: `1px solid ${C.purpleBorder}`,
                    padding: "10px 14px", marginBottom: "12px",
                    fontSize: "15px", fontWeight: 800, color: C.purple, letterSpacing: "0.5px"
                }}>
                    {grammarNote.structure_formula}
                </div>
            )}

            <div className="em-grammar-body" style={{
                ...insetNeu(),
                padding: "18px 16px", marginBottom: "12px",
                textAlign: "left", lineHeight: 1.8, color: C.text, fontSize: "13.5px"
            }}>
                {splitNote(grammarNote.text).map((line, i) => (
                    <p key={i} style={{ margin: i === 0 ? "0 0 10px" : "0 0 10px" }}>
                        {parseBold(line)}
                    </p>
                ))}
            </div>

            {grammarNote.quick_tip && (
                <div className="em-grammar-tip" style={{
                    ...insetNeu(),
                    border: "1px solid rgba(61,235,160,0.2)",
                    padding: "10px 14px", marginBottom: "18px",
                    fontSize: "12px", color: C.muted, textAlign: "left", lineHeight: 1.6
                }}>
                    💡 {grammarNote.quick_tip}
                </div>
            )}

            <button onClick={onNext} style={{ ...btnStyle(C.accentFill, "#000"), width: "100%", fontSize: "15px", padding: "15px" }}>
                ¡Lo entendí! ⚡ Empezar ejercicios
            </button>
        </div>
    );
}

/* ─── QUIZ ────────────────────────────────────────────────────────────────── */
// Exportada para el Consultorio. Las tarjetas de ejercicio son la parte del
// motor que mas se ha pulido —accesibilidad, feedback, light/dark— y duplicarlas
// para otra pantalla significaria arreglar cada fallo dos veces.
export function QuizCard({ exercise, onCorrect, onWrong, nivel }) {
    const [sel, setSel] = useState(null);
    const [rev, setRev] = useState(false);
    const pick = opt => {
        if (rev) return;
        setSel(opt); setRev(true);
        setTimeout(() => opt === exercise.answer ? onCorrect() : onWrong(), 950);
    };
    return (
        <div>
            <p style={{
                fontSize: "10px", fontWeight: 700, color: C.accent, letterSpacing: "2px",
                textTransform: "uppercase", marginBottom: "12px"
            }}>{T(nivel, "quiz")}</p>
            <p style={{ fontSize: "17px", fontWeight: 700, color: C.text, lineHeight: 1.55, marginBottom: "20px" }}>
                {parseBold(exercise.question)}
            </p>
            <div style={{ display: "grid", gap: "9px" }}>
                {(exercise.options || []).map(opt => {
                    let shadow = "-3px 3px 6px var(--shadow-dark), 3px -3px 6px var(--shadow-light)";
                    let bg = C.bgCard, color = C.text;
                    if (rev && opt === exercise.answer) { bg = C.greenDim; color = C.green; shadow = `inset -3px 3px 6px var(--shadow-dark), inset 3px -3px 6px var(--shadow-light), 0 0 8px ${alpha(C.green, 27)}`; }
                    else if (rev && opt === sel && opt !== exercise.answer) { bg = C.redDim; color = C.red; shadow = `inset -3px 3px 6px var(--shadow-dark), inset 3px -3px 6px var(--shadow-light)`; }
                    return (
                        <button key={opt} onClick={() => pick(opt)} style={{
                            background: bg, borderRadius: "12px",
                            padding: "13px 16px", fontSize: "14px", fontWeight: 600, color, cursor: rev ? "default" : "pointer",
                            textAlign: "left", transition: "all 0.25s", fontFamily: "inherit",
                            border: "none", boxShadow: shadow
                        }}>
                            {opt}{rev && opt === exercise.answer && " ✓"}{rev && opt === sel && opt !== exercise.answer && " ✗"}
                        </button>
                    );
                })}
            </div>
            {rev && exercise.explanation && (
                <div style={{
                    marginTop: "12px", ...insetNeu(), padding: "11px 14px", fontSize: "12px", color: C.muted, lineHeight: 1.6
                }}>
                    {parseBold(exercise.explanation)}
                </div>
            )}
        </div>
    );
}

/* ─── FILL BLANK ──────────────────────────────────────────────────────────── */
// Exportada para el Consultorio. Las tarjetas de ejercicio son la parte del
// motor que mas se ha pulido —accesibilidad, feedback, light/dark— y duplicarlas
// para otra pantalla significaria arreglar cada fallo dos veces.
export function FillBlankCard({ exercise, onCorrect, onWrong, nivel }) {
    const [chosen, setChosen] = useState(null);
    const [rev, setRev] = useState(false);
    const pick = opt => {
        if (rev) return;
        setChosen(opt); setRev(true);
        setTimeout(() => opt === exercise.answer ? onCorrect() : onWrong(), 950);
    };
    const sentence = exercise.sentence || "";
    return (
        <div>
            <p style={{
                fontSize: "10px", fontWeight: 700, color: C.accent, letterSpacing: "2px",
                textTransform: "uppercase", marginBottom: "12px"
            }}>{T(nivel, "fillBlank")}</p>
            <div style={{
                ...insetNeu(),
                padding: "18px", marginBottom: "8px", fontSize: "18px", fontWeight: 700, color: C.text,
                textAlign: "center", minHeight: "56px", display: "flex", alignItems: "center",
                justifyContent: "center", flexWrap: "wrap", gap: "4px", lineHeight: 1.5
            }}>
                {rev ? (
                    sentence.split("___").flatMap((part, i, arr) =>
                        i < arr.length - 1 ? [
                            <span key={`p${i}`}>{part}</span>,
                            <mark key={`m${i}`} style={{
                                background: chosen === exercise.answer ? C.greenDim : C.redDim,
                                color: chosen === exercise.answer ? C.green : C.red, padding: "0 8px", borderRadius: "6px", fontStyle: "normal"
                            }}>
                                {chosen}
                            </mark>
                        ] : [<span key={`p${i}`}>{part}</span>]
                    )
                ) : sentence}
            </div>
            {exercise.context && (
                <p style={{ fontSize: "11px", color: C.faint, marginBottom: "16px", textAlign: "center" }}>
                    💡 {exercise.context}
                </p>
            )}
            <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                {(exercise.options || []).map(opt => {
                    let shadow = "-3px 3px 6px var(--shadow-dark), 3px -3px 6px var(--shadow-light)";
                    let bg = C.bgCard, color = C.muted;
                    if (rev && opt === exercise.answer) { bg = C.greenDim; color = C.green; shadow = "inset -3px 3px 6px var(--shadow-dark), inset 3px -3px 6px var(--shadow-light)"; }
                    else if (rev && opt === chosen && opt !== exercise.answer) { bg = C.redDim; color = C.red; shadow = "inset -3px 3px 6px var(--shadow-dark), inset 3px -3px 6px var(--shadow-light)"; }
                    return (
                        <button key={opt} onClick={() => pick(opt)} style={{
                            background: bg, borderRadius: "12px",
                            padding: "12px 16px", fontSize: "15px", fontWeight: 700, color, cursor: rev ? "default" : "pointer",
                            fontFamily: "inherit", transition: "all 0.25s", flex: 1, border: "none", boxShadow: shadow
                        }}>
                            {opt}
                        </button>
                    );
                })}
            </div>
            {rev && exercise.explanation && (
                <div style={{
                    marginTop: "12px", ...insetNeu(), padding: "11px 14px", fontSize: "12px", color: C.muted, lineHeight: 1.6
                }}>
                    {parseBold(exercise.explanation)}
                </div>
            )}
        </div>
    );
}

/* ─── WORD ORDER ──────────────────────────────────────────────────────────── */
// Exportada para el Consultorio. Las tarjetas de ejercicio son la parte del
// motor que mas se ha pulido —accesibilidad, feedback, light/dark— y duplicarlas
// para otra pantalla significaria arreglar cada fallo dos veces.
export function WordOrderCard({ exercise, onCorrect, onWrong, nivel }) {
    const [available, setAvailable] = useState(() => [...(exercise.words || [])]);
    const [built, setBuilt] = useState([]);
    const [checked, setChecked] = useState(false);
    const [isCorrect, setIsCorrect] = useState(false);

    const addWord = word => {
        if (checked) return;
        setBuilt(b => [...b, word]);
        setAvailable(a => { const i = a.indexOf(word); return [...a.slice(0, i), ...a.slice(i + 1)]; });
    };
    const removeWord = idx => {
        if (checked) return;
        const word = built[idx];
        setBuilt(b => [...b.slice(0, idx), ...b.slice(idx + 1)]);
        setAvailable(a => [...a, word]);
    };
    const verify = () => {
        if (built.length === 0) return;
        const attempt = built.join(" ");
        const correct = attempt.toLowerCase().trim() === exercise.answer.toLowerCase().trim();
        setIsCorrect(correct); setChecked(true);
        setTimeout(() => correct ? onCorrect() : onWrong(), 1000);
    };
    const reset = () => { setAvailable([...(exercise.words || [])]); setBuilt([]); setChecked(false); setIsCorrect(false); };

    return (
        <div>
            <p style={{
                fontSize: "10px", fontWeight: 700, color: C.blue, letterSpacing: "2px",
                textTransform: "uppercase", marginBottom: "12px"
            }}>{T(nivel, "wordOrder")}</p>

            <div style={{
                minHeight: "56px", padding: "12px 14px", marginBottom: "12px",
                display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center",
                transition: "all 0.3s",
                ...insetNeu({ borderRadius: "13px" }),
                boxShadow: checked
                    ? (isCorrect
                        ? `inset -4px 4px 8px var(--shadow-dark), inset 4px -4px 8px var(--shadow-light), 0 0 10px ${alpha(C.green, 27)}`
                        : `inset -4px 4px 8px var(--shadow-dark), inset 4px -4px 8px var(--shadow-light), 0 0 10px ${alpha(C.red, 27)}`)
                    : "inset -4px 4px 8px var(--shadow-dark), inset 4px -4px 8px var(--shadow-light)"
            }}>
                {built.length === 0 ? (
                    <span style={{ fontSize: "12px", color: C.faint, fontStyle: "italic" }}>
                        Toca las palabras para construir la oración...
                    </span>
                ) : built.map((w, i) => (
                    <button key={i} onClick={() => removeWord(i)}
                        style={{
                            background: checked ? (isCorrect ? C.greenDim : C.redDim) : C.accentDim,
                            borderRadius: "8px", padding: "6px 12px", fontSize: "14px", fontWeight: 700,
                            color: checked ? (isCorrect ? C.green : C.red) : C.accent,
                            cursor: checked ? "default" : "pointer", fontFamily: "inherit", transition: "all 0.2s",
                            border: "none", boxShadow: "-2px 2px 4px var(--shadow-dark), 2px -2px 4px var(--shadow-light)"
                        }}>
                        {w}
                    </button>
                ))}
            </div>

            <div className="em-word-available" style={{ marginBottom: "16px", minHeight: "44px" }}>
                {available.map((w, i) => (
                    <button key={i} onClick={() => addWord(w)}
                        style={{
                            background: C.blueDim, borderRadius: "8px", padding: "8px 14px", fontSize: "14px", fontWeight: 700,
                            color: C.blue, cursor: "pointer", fontFamily: "inherit", transition: "all 0.18s",
                            border: "none", boxShadow: "-3px 3px 6px var(--shadow-dark), 3px -3px 6px var(--shadow-light)"
                        }}>
                        {w}
                    </button>
                ))}
            </div>

            {checked && !isCorrect && (
                <div style={{
                    ...insetNeu(), padding: "11px 14px", marginBottom: "12px",
                    fontSize: "13px", color: C.muted
                }}>
                    ✓ <strong style={{ color: C.green }}>{exercise.answer}</strong>
                    {exercise.hint && <p style={{ fontSize: "11px", marginTop: "6px", color: C.faint }}>💡 {exercise.hint}</p>}
                </div>
            )}

            <div style={{ display: "flex", gap: "8px" }}>
                {!checked && (
                    <>
                        <button onClick={reset} style={{
                            background: "var(--bg-secondary)", borderRadius: "12px", padding: "11px",
                            fontSize: "12px", color: C.muted, cursor: "pointer", fontFamily: "inherit", fontWeight: 700,
                            border: "none", boxShadow: "-3px 3px 6px var(--shadow-dark), 3px -3px 6px var(--shadow-light)"
                        }}>
                            🔄 Reset
                        </button>
                        <button onClick={verify} disabled={built.length === 0}
                            style={{ ...btnStyle(C.blue, "#fff", built.length === 0), flex: 1, fontSize: "14px", padding: "12px" }}>
                            Verificar ✓
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

/* ─── ERROR DETECT ────────────────────────────────────────────────────────── */
// Exportada para el Consultorio. Las tarjetas de ejercicio son la parte del
// motor que mas se ha pulido —accesibilidad, feedback, light/dark— y duplicarlas
// para otra pantalla significaria arreglar cada fallo dos veces.
export function ErrorDetectCard({ exercise, onCorrect, onWrong, nivel }) {
    const [chosen, setChosen] = useState(null);
    const [rev, setRev] = useState(false);
    const pick = opt => {
        if (rev) return;
        setChosen(opt); setRev(true);
        setTimeout(() => opt === exercise.answer ? onCorrect() : onWrong(), 950);
    };
    return (
        <div>
            <p style={{
                fontSize: "10px", fontWeight: 700, color: C.accent, letterSpacing: "2px",
                textTransform: "uppercase", marginBottom: "14px"
            }}>{T(nivel, "errorDetect")}</p>
            <div style={{
                ...insetNeu(),
                padding: "20px",
                marginBottom: "18px", fontSize: "17px", fontWeight: 600, color: C.text,
                textAlign: "center", fontStyle: "italic", lineHeight: 1.55
            }}>
                "{exercise.sentence}"
            </div>
            {rev && (
                <div style={{
                    background: chosen === exercise.answer ? C.greenDim : C.redDim,
                    borderRadius: "11px", padding: "12px 15px", marginBottom: "16px",
                    fontSize: "12px", color: C.muted, lineHeight: 1.65,
                    boxShadow: "inset -3px 3px 6px var(--shadow-dark), inset 3px -3px 6px var(--shadow-light)"
                }}>
                    {exercise.correction && <p style={{ marginBottom: "4px" }}>✓ {parseBold(exercise.correction)}</p>}
                    {parseBold(exercise.explanation || "")}
                </div>
            )}
            <div style={{ display: "flex", gap: "8px" }}>
                {["Correct", "Incorrect"].map(opt => {
                    let shadow = "-3px 3px 6px var(--shadow-dark), 3px -3px 6px var(--shadow-light)";
                    let bg = C.bgCard, color = C.muted;
                    if (rev && opt === exercise.answer) { bg = C.greenDim; color = C.green; shadow = "inset -3px 3px 6px var(--shadow-dark), inset 3px -3px 6px var(--shadow-light)"; }
                    else if (rev && opt === chosen && opt !== exercise.answer) { bg = C.redDim; color = C.red; shadow = "inset -3px 3px 6px var(--shadow-dark), inset 3px -3px 6px var(--shadow-light)"; }
                    return (
                        <button key={opt} onClick={() => pick(opt)} style={{
                            background: bg, borderRadius: "12px",
                            padding: "14px", fontSize: "14px", fontWeight: 700, color, cursor: rev ? "default" : "pointer",
                            fontFamily: "inherit", transition: "all 0.25s", flex: 1, border: "none", boxShadow: shadow
                        }}>
                            {opt === "Correct" ? "✓ Correct" : "✗ Incorrect"}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/* ─── MATCHING ────────────────────────────────────────────────────────────── */
// Exportada para el Consultorio. Las tarjetas de ejercicio son la parte del
// motor que mas se ha pulido —accesibilidad, feedback, light/dark— y duplicarlas
// para otra pantalla significaria arreglar cada fallo dos veces.
export function MatchingCard({ exercise, onCorrect, nivel }) {
    // matched: { [leftIndex]: rightIndex }
    const [matched, setMatched] = useState({});
    // sel: leftIndex or null
    const [sel, setSel] = useState(null);
    const [errors, setErrors] = useState({});
    const [shuffledRight] = useState(() => {
        const pairs = (exercise.pairs || []).map((p, i) => ({ str: p.right, origIdx: i }));
        for (let i = pairs.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [pairs[i], pairs[j]] = [pairs[j], pairs[i]];
        }
        return pairs;
    });

    const pickLeft = lIdx => {
        if (matched[lIdx] !== undefined) return;
        setSel(s => s === lIdx ? null : lIdx);
    };

    const pickRight = (rStr, rIdx) => {
        if (sel === null) return;
        if (Object.values(matched).includes(rIdx)) return;

        const expectedStr = exercise.pairs[sel].right;
        if (rStr === expectedStr) {
            const nm = { ...matched, [sel]: rIdx };
            setMatched(nm); setSel(null);
            if (Object.keys(nm).length === (exercise.pairs || []).length) setTimeout(onCorrect, 600);
        } else {
            setErrors(e => ({ ...e, [sel]: true }));
            setTimeout(() => { setErrors({}); setSel(null); }, 700);
        }
    };

    const getLeftStyles = (isM, isSel, hasErr) => {
        let base = {
            borderRadius: "11px", padding: "9px 11px", fontSize: "13px", fontWeight: 700,
            cursor: isM ? "default" : "pointer", fontFamily: "inherit",
            transition: "all 0.2s", textAlign: "center",
        };
        if (isM) {
            return { ...base, background: C.greenDim, color: C.green, border: `2px solid ${C.green}`, boxShadow: "none" };
        }
        if (hasErr) {
            return { ...base, background: C.redDim, color: C.red, border: `2px solid ${C.red}`, boxShadow: "none" };
        }
        if (isSel) {
            return { ...base, background: C.accentDim, color: C.accent, border: `2px solid ${C.accentBorder}`, boxShadow: "none" };
        }
        return {
            ...base, background: C.bgCard, color: C.text, border: "2px solid transparent",
            boxShadow: "-3px 3px 6px var(--shadow-dark), 3px -3px 6px var(--shadow-light)"
        };
    };

    const getRightStyles = (isM) => {
        let base = {
            borderRadius: "11px", padding: "9px 11px", fontSize: "13px", fontWeight: 700,
            cursor: isM ? "default" : "pointer", fontFamily: "inherit",
            transition: "all 0.2s", textAlign: "center",
        };
        if (isM) {
            return { ...base, background: C.greenDim, color: C.green, border: `2px solid ${C.green}`, boxShadow: "none" };
        }
        return {
            ...base, background: C.bgCard, color: C.text, border: "2px solid transparent",
            boxShadow: "-3px 3px 6px var(--shadow-dark), 3px -3px 6px var(--shadow-light)"
        };
    };

    return (
        <div>
            <p style={{
                fontSize: "10px", fontWeight: 700, color: C.accent, letterSpacing: "2px",
                textTransform: "uppercase", marginBottom: "10px"
            }}>{T(nivel, "matching")}</p>
            {exercise.instruction && (
                <p style={{ fontSize: "12px", color: C.muted, marginBottom: "14px" }}>{exercise.instruction}</p>
            )}
            <div className="em-matching-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", minWidth: 0, padding: "4px" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "10px", minWidth: 0 }}>
                    {(exercise.pairs || []).map((p, i) => {
                        const isM = matched[i] !== undefined;
                        const isSel = sel === i;
                        const hasErr = errors[i];
                        return (
                            <button key={`L_${i}`} onClick={() => pickLeft(i)} style={getLeftStyles(isM, isSel, hasErr)}>
                                {p.left}{isM && " ✓"}
                            </button>
                        );
                    })}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "10px", minWidth: 0 }}>
                    {shuffledRight.map((p, i) => {
                        const isM = Object.values(matched).includes(i);
                        return (
                            <button key={`R_${i}`} onClick={() => pickRight(p.str, i)} style={getRightStyles(isM)}>
                                {p.str}{isM && " ✓"}
                            </button>
                        );
                    })}
                </div>
            </div>
            <p style={{ fontSize: "10px", color: C.faint, textAlign: "center", marginTop: "10px" }}>
                ← Izquierda primero, luego derecha →
            </p>
        </div>
    );
}

/* ─── LISTENING ───────────────────────────────────────────────────────────── */
function ListeningCard({ exercise, onCorrect, onWrong, nivel }) {
    const [phase, setPhase] = useState("ready");
    const [sel, setSel] = useState(null);
    const [rev, setRev] = useState(false);
    const speak = () => {
        if (!("speechSynthesis" in window)) return;
        window.speechSynthesis.cancel();
        const u = new SpeechSynthesisUtterance(exercise.audio_text);
        u.lang = "en-US"; u.rate = 0.85; u.pitch = 1;
        window.speechSynthesis.speak(u);
    };
    const play = () => { setPhase("playing"); speak(); setTimeout(() => setPhase("question"), 2800); };
    const pick = opt => {
        if (rev) return;
        setSel(opt); setRev(true);
        setTimeout(() => opt === exercise.answer ? onCorrect() : onWrong(), 950);
    };
    return (
        <div>
            <p style={{
                fontSize: "10px", fontWeight: 700, color: C.blue, letterSpacing: "2px",
                textTransform: "uppercase", marginBottom: "12px"
            }}>{T(nivel, "listening")}</p>
            {phase === "ready" && (
                <div style={{ textAlign: "center", padding: "16px 0 24px" }}>
                    <div style={{ fontSize: "52px", marginBottom: "14px" }}>🎧</div>
                    <p style={{ color: C.muted, fontSize: "13px", marginBottom: "22px" }}>{exercise.question}</p>
                    <button onClick={play} style={{ ...btnStyle(C.blue, "#fff"), fontSize: "14px", padding: "13px 28px" }}>
                        ▶ Reproducir audio
                    </button>
                </div>
            )}
            {phase === "playing" && (
                <div style={{ textAlign: "center", padding: "20px 0 28px" }}>
                    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "center", gap: "5px", height: "36px", marginBottom: "14px" }}>
                        {[0.4, 0.7, 1, 0.85, 0.55, 0.9, 0.65, 1, 0.75, 0.5].map((h, i) => (
                            <div key={i} className="em-wave-bar" style={{
                                width: "5px", background: C.blue, borderRadius: "3px",
                                animation: `waveBar 0.6s ${i * 0.07}s ease-in-out infinite alternate`,
                                height: `${h * 32}px`, opacity: 0.85
                            }} />
                        ))}
                    </div>
                    <p style={{ color: C.blue, fontSize: "13px", fontWeight: 800 }}>Escuchando...</p>
                    <p style={{ color: C.faint, fontSize: "11px", fontStyle: "italic", marginTop: "4px" }}>"{exercise.audio_text}"</p>
                </div>
            )}
            {phase === "question" && (
                <div>
                    <div style={{
                        ...insetNeu(),
                        padding: "14px 16px", marginBottom: "16px", display: "flex", gap: "10px", alignItems: "center"
                    }}>
                        <button onClick={speak} style={{
                            background: "var(--bg-primary)", borderRadius: "8px", padding: "6px 10px",
                            cursor: "pointer", fontSize: "14px", flexShrink: 0, border: "none",
                            boxShadow: "-2px 2px 4px var(--shadow-dark), 2px -2px 4px var(--shadow-light)"
                        }}>🔁</button>
                        <p style={{ fontSize: "14px", fontStyle: "italic", color: C.text, lineHeight: 1.5 }}>{exercise.audio_text}</p>
                    </div>
                    <p style={{ fontSize: "14px", fontWeight: 700, color: C.text, marginBottom: "14px" }}>{exercise.question}</p>
                    <div style={{ display: "grid", gap: "8px" }}>
                        {(exercise.options || []).map(opt => {
                            let shadow = "-3px 3px 6px var(--shadow-dark), 3px -3px 6px var(--shadow-light)";
                            let bg = C.bgCard, color = C.text;
                            if (rev && opt === exercise.answer) { bg = C.greenDim; color = C.green; shadow = "inset -3px 3px 6px var(--shadow-dark), inset 3px -3px 6px var(--shadow-light)"; }
                            else if (rev && opt === sel && opt !== exercise.answer) { bg = C.redDim; color = C.red; shadow = "inset -3px 3px 6px var(--shadow-dark), inset 3px -3px 6px var(--shadow-light)"; }
                            return (
                                <button key={opt} onClick={() => pick(opt)} style={{
                                    background: bg, borderRadius: "12px",
                                    padding: "13px 16px", fontSize: "14px", fontWeight: 600, color, cursor: rev ? "default" : "pointer",
                                    textAlign: "left", transition: "all 0.25s", fontFamily: "inherit",
                                    border: "none", boxShadow: shadow
                                }}>
                                    {opt}{rev && opt === exercise.answer && " ✓"}{rev && opt === sel && opt !== exercise.answer && " ✗"}
                                </button>
                            );
                        })}
                    </div>
                    {rev && exercise.explanation && (
                        <div style={{ marginTop: "12px", ...insetNeu(), padding: "11px 14px", fontSize: "12px", color: C.muted, lineHeight: 1.6 }}>
                            {exercise.explanation}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/* ─── PRONUNCIATION ───────────────────────────────────────────────────────── */
// ⚠️ SUSTITUIDA — no se usa. La reemplaza `PronunciationScoredCard` de
// VoiceLabCards.jsx, que puntua con el oido de Deepgram en vez de comparar
// cadenas con SpeechRecognition del navegador.
//
// Se queda aqui como registro de un fallo que costo encontrar: el `setTimeout`
// de 6 segundos llamaba a `rec.stop()`, y si el navegador no habia reconocido
// nada eso dispara `onend` — que no tenia manejador. La fase se quedaba en
// "listening" para siempre, sin nota y sin forma de avanzar.
//
// Si alguien la revive, que ponga primero un `rec.onend`.
function PronunciationCard({ exercise, onCorrect, onWrong, onSkip, nivel }) {
    const [phase, setPhase] = useState("ready");
    const [heard, setHeard] = useState("");
    const [isCorrect, setIsCorrect] = useState(null);
    const recRef = useRef(null);
    const timerRef = useRef(null);
    useEffect(() => () => { clearTimeout(timerRef.current); try { recRef.current?.stop(); } catch { } }, []);
    const speakWord = () => {
        if (!("speechSynthesis" in window)) return;
        window.speechSynthesis.cancel();
        const u = new SpeechSynthesisUtterance(exercise.word);
        u.lang = "en-US"; u.rate = 0.75; window.speechSynthesis.speak(u);
    };
    const startListening = () => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) { setHeard("Tu navegador no soporta reconocimiento de voz."); setIsCorrect(null); setPhase("result"); return; }
        const rec = new SR(); rec.lang = "en-US"; rec.interimResults = false; rec.maxAlternatives = 5;
        recRef.current = rec;
        rec.onresult = e => {
            clearTimeout(timerRef.current);
            const transcripts = Array.from(e.results[0]).map(r => r.transcript.trim().toLowerCase().replace(/[^a-z\s'-]/g, ""));
            const target = (exercise.word || "").trim().toLowerCase();
            const matched = transcripts.some(t => t === target || t.split(" ").includes(target) || t.startsWith(target));
            setHeard(e.results[0][0].transcript); setIsCorrect(matched); setPhase("result");
            setTimeout(() => matched ? onCorrect() : onWrong(), 1400);
        };
        rec.onerror = ev => {
            clearTimeout(timerRef.current);
            setHeard(ev.error === "no-speech" ? "No detecté voz." : ev.error === "not-allowed" ? "Permiso denegado." : `Error: ${ev.error}`);
            setIsCorrect(false); setPhase("result");
        };
        setPhase("listening");
        try { rec.start(); } catch { setPhase("ready"); return; }
        timerRef.current = setTimeout(() => { try { rec.stop(); } catch { } }, 6000);
    };
    const retry = () => { setPhase("ready"); setHeard(""); setIsCorrect(null); };
    return (
        <div>
            <p style={{
                fontSize: "10px", fontWeight: 700, color: C.purple, letterSpacing: "2px",
                textTransform: "uppercase", marginBottom: "14px"
            }}>{T(nivel, "speaking")}</p>
            <div style={{ textAlign: "center", marginBottom: "18px" }}>
                <div style={{ fontSize: "50px", fontWeight: 900, color: C.text, letterSpacing: "3px", marginBottom: "6px" }}>
                    {exercise.word}
                </div>
                {exercise.phonetic && (
                    <div style={{ fontSize: "16px", color: C.purple, marginBottom: "10px", fontFamily: "monospace" }}>
                        {exercise.phonetic}
                    </div>
                )}
                <button onClick={speakWord} style={{
                    background: C.purpleDim, borderRadius: "20px", padding: "8px 18px",
                    cursor: "pointer", fontSize: "12px", fontWeight: 700,
                    color: C.purple, fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: "7px",
                    border: "none", boxShadow: "-3px 3px 6px var(--shadow-dark), 3px -3px 6px var(--shadow-light)"
                }}>
                    🔊 Escuchar modelo nativo
                </button>
                {exercise.context && <p style={{ fontSize: "11px", color: C.faint, marginTop: "8px" }}>{exercise.context}</p>}
            </div>
            {exercise.tip && (
                <div style={{
                    ...insetNeu(), border: `1px solid ${C.purpleBorder}`,
                    padding: "10px 14px", marginBottom: "18px", fontSize: "12px", color: C.muted, lineHeight: 1.65
                }}>
                    💡 {exercise.tip}
                </div>
            )}
            {phase === "ready" && (
                <div style={{ textAlign: "center", padding: "6px 0 10px" }}>
                    <button onClick={startListening}
                        style={{
                            width: "84px", height: "84px", borderRadius: "50%",
                            background: "var(--bg-primary)", cursor: "pointer", fontSize: "34px",
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            marginBottom: "12px", border: "none",
                            boxShadow: "-8px 8px 16px var(--shadow-dark), 8px -8px 16px var(--shadow-light)"
                        }}>🎤</button>
                    <p style={{ fontSize: "13px", color: C.muted, fontWeight: 700 }}>Toca el micrófono y pronuncia</p>
                </div>
            )}
            {phase === "listening" && (
                <div style={{ textAlign: "center", padding: "6px 0 10px" }}>
                    <div style={{
                        width: "84px", height: "84px", borderRadius: "50%",
                        background: C.redDim, fontSize: "28px",
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        animation: "ping 0.85s ease-in-out infinite", marginBottom: "12px",
                        boxShadow: `0 0 20px ${alpha(C.red, 27)}`
                    }}>⏹️</div>
                    <p style={{ fontSize: "13px", color: C.red, fontWeight: 900 }}>🔴 Escuchando...</p>
                </div>
            )}
            {phase === "result" && (
                <div>
                    <div style={{
                        ...insetNeu(),
                        padding: "18px", marginBottom: "12px", textAlign: "center"
                    }}>
                        <div style={{ fontSize: "32px", marginBottom: "8px" }}>{isCorrect === null ? "⚠️" : isCorrect ? "🎉" : "🔄"}</div>
                        {heard && <p style={{
                            fontSize: "20px", fontWeight: 800,
                            color: isCorrect === null ? C.text : isCorrect ? C.green : C.red, marginBottom: "8px"
                        }}>"{heard}"</p>}
                        {isCorrect && <p style={{ fontSize: "13px", color: C.green, fontWeight: 700 }}>¡Pronunciación perfecta! 🔥</p>}
                        {isCorrect === false && <p style={{ fontSize: "11px", color: C.faint }}>¡Practica y lo dominarás! 💪</p>}
                    </div>
                    {(isCorrect === false || isCorrect === null) && (
                        <div style={{ display: "flex", gap: "8px" }}>
                            <button onClick={retry} style={{
                                ...btnStyle(C.purpleDim, C.purple),
                                flex: 1, fontSize: "13px", padding: "12px"
                            }}>
                                🎤 Intentar de nuevo
                            </button>
                            <button onClick={onSkip} style={{
                                background: "var(--bg-secondary)", borderRadius: "14px", flex: 1, fontSize: "12px",
                                padding: "12px", color: C.faint, cursor: "pointer", fontFamily: "inherit", fontWeight: 700,
                                border: "none", boxShadow: "-3px 3px 6px var(--shadow-dark), 3px -3px 6px var(--shadow-light)"
                            }}>
                                Omitir →
                            </button>
                        </div>
                    )}
                </div>
            )}
            {(phase === "ready" || phase === "listening") && (
                <div style={{ textAlign: "center", marginTop: "16px", paddingTop: "12px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                    <button onClick={onSkip} style={{
                        background: "transparent", border: "none",
                        borderRadius: "20px", padding: "7px 22px", cursor: "pointer", fontSize: "11px", color: C.faint, fontFamily: "inherit"
                    }}>
                        ⏭ Omitir pronunciación
                    </button>
                </div>
            )}
        </div>
    );
}

/* ─── READING ─────────────────────────────────────────────────────────────── */
function ReadingCard({ exercise, onCorrect, onWrong }) {
    const [sel, setSel] = useState(null);
    const [rev, setRev] = useState(false);
    const pick = opt => {
        if (rev) return;
        setSel(opt); setRev(true);
        setTimeout(() => opt === exercise.answer ? onCorrect() : onWrong(), 950);
    };
    return (
        <div>
            <p style={{
                fontSize: "10px", fontWeight: 700, color: C.green, letterSpacing: "2px",
                textTransform: "uppercase", marginBottom: "12px"
            }}>📚 Lee y comprende</p>
            <div style={{
                ...insetNeu(),
                padding: "16px", marginBottom: "16px",
                fontSize: "14px", color: C.muted, lineHeight: 1.8
            }}>
                {exercise.passage}
            </div>
            <p style={{ fontSize: "15px", fontWeight: 700, color: C.text, marginBottom: "14px" }}>{exercise.question}</p>
            <div style={{ display: "grid", gap: "8px" }}>
                {(exercise.options || []).map(opt => {
                    let shadow = "-3px 3px 6px var(--shadow-dark), 3px -3px 6px var(--shadow-light)";
                    let bg = C.bgCard, color = C.text;
                    if (rev && opt === exercise.answer) { bg = C.greenDim; color = C.green; shadow = "inset -3px 3px 6px var(--shadow-dark), inset 3px -3px 6px var(--shadow-light)"; }
                    else if (rev && opt === sel && opt !== exercise.answer) { bg = C.redDim; color = C.red; shadow = "inset -3px 3px 6px var(--shadow-dark), inset 3px -3px 6px var(--shadow-light)"; }
                    return (
                        <button key={opt} onClick={() => pick(opt)} style={{
                            background: bg, borderRadius: "12px",
                            padding: "13px 16px", fontSize: "14px", fontWeight: 600, color, cursor: rev ? "default" : "pointer",
                            textAlign: "left", transition: "all 0.25s", fontFamily: "inherit",
                            border: "none", boxShadow: shadow
                        }}>
                            {opt}{rev && opt === exercise.answer && " ✓"}{rev && opt === sel && opt !== exercise.answer && " ✗"}
                        </button>
                    );
                })}
            </div>
            {rev && exercise.explanation && (
                <div style={{ marginTop: "12px", ...insetNeu(), padding: "11px 14px", fontSize: "12px", color: C.muted, lineHeight: 1.6 }}>
                    {parseBold(exercise.explanation)}
                </div>
            )}
        </div>
    );
}

/* ─── WRITING ─────────────────────────────────────────────────────────────── */
function WritingCard({ exercise, onCorrect, onWrong, nivel }) {
    const [text, setText] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [isCorrect, setIsCorrect] = useState(false);
    const submit = () => {
        if (submitted || text.trim().length < 4) return;
        setSubmitted(true);
        const lower = text.toLowerCase();
        const kws = exercise.keywords || [];
        const hits = kws.filter(k => lower.includes(k.toLowerCase()));
        const ok = hits.length >= Math.max(1, Math.ceil(kws.length / 2));
        setIsCorrect(ok);
        setTimeout(() => ok ? onCorrect() : onWrong(), 1600);
    };
    return (
        <div>
            <p style={{
                fontSize: "10px", fontWeight: 700, color: C.orange, letterSpacing: "2px",
                textTransform: "uppercase", marginBottom: "12px"
            }}>{T(nivel, "writing")}</p>
            <div style={{
                ...insetNeu(),
                padding: "14px", marginBottom: "16px"
            }}>
                <p style={{ fontSize: "15px", fontWeight: 700, color: C.text, lineHeight: 1.55 }}>{exercise.prompt}</p>
                {exercise.hint && <p style={{ fontSize: "11px", color: C.faint, marginTop: "6px" }}>💡 {exercise.hint}</p>}
            </div>
            <textarea value={text} onChange={e => setText(e.target.value)} disabled={submitted}
                maxLength={LIMITES.respuestaEjercicio}
                placeholder="Type your answer in English..."
                rows={3} style={{
                    width: "100%", background: "var(--bg-secondary)",
                    borderRadius: "13px", padding: "13px 14px", color: C.text, fontSize: "14px",
                    fontFamily: "inherit", resize: "none", outline: "none", transition: "box-shadow 0.2s",
                    lineHeight: 1.6, marginBottom: "12px", boxSizing: "border-box", border: "none",
                    boxShadow: "inset -4px 4px 8px var(--shadow-dark), inset 4px -4px 8px var(--shadow-light)"
                }} />
            {submitted && (
                <div style={{
                    ...insetNeu(),
                    padding: "13px", marginBottom: "12px", lineHeight: 1.65,
                    boxShadow: isCorrect
                        ? `inset -3px 3px 6px var(--shadow-dark), inset 3px -3px 6px var(--shadow-light), 0 0 10px ${alpha(C.green, 20)}`
                        : "inset -3px 3px 6px var(--shadow-dark), inset 3px -3px 6px var(--shadow-light)"
                }}>
                    <p style={{ fontSize: "12px", fontWeight: 700, color: isCorrect ? C.green : C.red, marginBottom: "4px" }}>
                        {isCorrect ? "✓ ¡Excelente!" : "✗ Respuesta modelo:"}
                    </p>
                    <p style={{ fontSize: "13px", color: C.muted, fontStyle: "italic" }}>
                        "{exercise.model_answer}"
                    </p>
                </div>
            )}
            {!submitted && (
                <button onClick={submit} disabled={text.trim().length < 4}
                    style={{
                        ...btnStyle("rgba(255,140,66,0.9)", "#000", text.trim().length < 4),
                        width: "100%", fontSize: "14px", padding: "13px"
                    }}>
                    {T(nivel, "enviar")}
                </button>
            )}
        </div>
    );
}

/* ══════════════════════════════════════════════════════════════════════════════
   SCREEN COMPONENTS
   ══════════════════════════════════════════════════════════════════════════════ */

/* ─── ENGINE SETUP ────────────────────────────────────────────────────────── */
// `onGoClassic` abre el temario curado de esta misma lección: no es "otro
// modo" que el alumno tenga que entender, es la reserva para cuando ya no
// quedan tokens. `motorType`, `lessonId` y `navigate` salieron de la firma
// junto con el modal que saltaba entre mundos.
function EngineSetup({ lessonTitle, lessonLevel, onLaunch, onGoClassic }) {
    const [mode, setMode] = useState("academic");
    const [quickSelected, setQuickSelected] = useState(null);
    const [customInput, setCustomInput] = useState("");
    const [selectedLevel, setSelectedLevel] = useState(lessonLevel || "A1");
    

    const QUICK_CONTEXTS = [
        { id: "business", emoji: "💼", label: "Business", context: "Business and professional corporate environment" },
        { id: "hotel", emoji: "🏨", label: "Hotelería", context: "Hotel and hospitality industry, attending international guests" },
        { id: "restaurant", emoji: "🍽️", label: "Restaurante", context: "Restaurant and food service industry" },
        { id: "travel", emoji: "✈️", label: "Viaje", context: "Travel and tourism, airports and international trips" },
        { id: "health", emoji: "🏥", label: "Salud", context: "Healthcare and medical environment" },
        { id: "tech", emoji: "💻", label: "Tecnología", context: "Technology and software industry" },
    ];

    const isContextual = mode === "contextual";
    const canLaunch = mode === "academic" || quickSelected !== null || customInput.trim().length > 3;

    const handleQuickSelect = (id) => {
        setQuickSelected(prev => prev === id ? null : id);
        setCustomInput("");
    };

    const handleInputChange = (val) => {
        setCustomInput(val);
        if (val.trim().length > 0) setQuickSelected(null);
    };

    const levelGroup = (lvl) => {
        const code = (lvl || "").toUpperCase();
        if (["A1", "A2"].includes(code)) return "basic";
        if (["B1", "B2"].includes(code)) return "intermediate";
        if (["C1", "C2"].includes(code)) return "advanced";
        return "basic";
    };

    const levelColor = (group) => {
        if (group === "basic") return C.green;
        if (group === "intermediate") return "var(--em-fire-warn)";
        return C.red;
    };

    const levelEmoji = (group) => {
        if (group === "basic") return "🟢";
        if (group === "intermediate") return "🟡";
        return "🔴";
    };

    const levelLabel = (group) => {
        if (group === "basic") return "Básico (A1 - A2)";
        if (group === "intermediate") return "Intermedio (B1 - B2)";
        return "Avanzado (C1 - C2)";
    };

    const handleLaunch = () => {
        let context = "General academic English learner";
        if (mode === "contextual") {
            // Los DOS, no uno u otro. Antes, elegir "Restaurante" descartaba lo
            // que el alumno hubiera escrito, asi que "soy mesero en Cancun" se
            // perdia y la IA solo veia "Restaurant and food service industry".
            // Sumarlos le da a Spacy el sector Y la persona.
            const partes = [];
            if (quickSelected) {
                const found = QUICK_CONTEXTS.find(q => q.id === quickSelected);
                partes.push(found?.context || quickSelected);
            }
            if (customInput.trim()) partes.push(customInput.trim());
            if (partes.length) context = partes.join(". ");
        }

        const chosenGroup = levelGroup(selectedLevel);
        const difficultyMap = { basic: "easy", intermediate: "medium", advanced: "hard" };
        const finalLevel = selectedLevel || lessonLevel;

        onLaunch({
            mode,
            context,
            objective: lessonTitle,
            difficulty: difficultyMap[chosenGroup],
            level_code: finalLevel,
        });
    };

    const selectLevel = (lvl) => {
        setSelectedLevel(lvl);
    };

    return (
        <>
            {/* El modal "Cambiar a Modo Clasico" vivia aqui. Se retiro con el
                selector de modos: ya no hay otro mundo al que ir, y Spacy es
                un paso de esta misma leccion. */}
            <div style={{
                padding: "24px 20px 52px",
                maxWidth: "clamp(520px, 60vw, 720px)",
                margin: "0 auto",
                position: "relative",
                zIndex: 1,
            }}>

                {/* ── Header ──────────────────────────────────────────────── */}
                <div style={{ textAlign: "center", marginBottom: "28px" }}>
                    <div style={{
                        width: "58px", height: "58px", borderRadius: "18px", fontSize: "26px",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        margin: "0 auto 14px",
                        ...cardNeu({ borderRadius: "18px" })
                    }}>⚡</div>

                    <h1 style={{ fontSize: "22px", fontWeight: 900, color: C.text, marginBottom: "5px" }}>
                        Configura tu Clase
                    </h1>
                    <p style={{ fontSize: "13px", color: C.muted, lineHeight: 1.5 }}>
                        La IA adapta cada ejercicio a tu modo de práctica
                    </p>

                    {/* Lección tag */}
                    <div style={{
                        display: "inline-flex", alignItems: "center", gap: "6px",
                        background: C.purpleDim, borderRadius: "20px",
                        padding: "4px 14px", marginTop: "10px",
                        boxShadow: "-2px 2px 4px var(--shadow-dark), 2px -2px 4px var(--shadow-light)"
                    }}>
                        <span style={{ fontSize: "10px", color: C.purple, fontWeight: 700 }}>
                            🎓 {lessonTitle}
                        </span>
                    </div>

                    {/* Nivel de la lección + selector */}
                    <div style={{
                        display: "flex", alignItems: "center", justifyContent: "center",
                        gap: "12px", marginTop: "16px",
                    }}>
                        <div style={{
                            display: "flex", alignItems: "center", gap: "6px",
                            background: "var(--bg-primary)",
                            borderRadius: "20px",
                            padding: "5px 14px",
                            boxShadow: "-3px 3px 6px var(--shadow-dark), 3px -3px 6px var(--shadow-light)",
                        }}>
                            <span style={{ fontSize: "14px" }}>
                                {levelEmoji(levelGroup(selectedLevel))}
                            </span>
                            <span style={{
                                fontSize: "10px", fontWeight: 800,
                                color: levelColor(levelGroup(selectedLevel)),
                            }}>
                                {selectedLevel}
                            </span>
                            <span style={{ fontSize: "9px", color: C.faint, marginLeft: "4px" }}>
                                · Nivel
                            </span>
                        </div>

                        
                    </div>
                </div>

                {/* ── Selector de modo ────────────────────────────────────── */}
                <p style={{
                    fontSize: "9px", fontWeight: 700, color: C.muted,
                    letterSpacing: "2px", textTransform: "uppercase",
                    marginBottom: "10px"
                }}>Modo de práctica</p>

                <div style={{ display: "flex", gap: "10px", marginBottom: "24px" }}>
                    {[
                        { value: "academic", emoji: "🎓", label: "Académico", desc: "Vocabulario y estructuras formales" },
                        { value: "contextual", emoji: "🌍", label: "Contextual", desc: "Basado en tu situación real" },
                    ].map(({ value, emoji, label, desc }) => {
                        const active = mode === value;
                        return (
                            <button
                                key={value}
                                onClick={() => { setMode(value); setQuickSelected(null); setCustomInput(""); }}
                                style={{
                                    flex: 1, background: "var(--bg-primary)",
                                    borderRadius: "16px", padding: "14px 12px",
                                    cursor: "pointer", fontFamily: "inherit",
                                    transition: "all 0.2s", textAlign: "left", border: "none",
                                    boxShadow: active
                                        ? "inset -4px 4px 8px var(--shadow-dark), inset 4px -4px 8px var(--shadow-light)"
                                        : "-4px 4px 8px var(--shadow-dark), 4px -4px 8px var(--shadow-light)",
                                }}
                            >
                                <div style={{
                                    fontSize: "13px", fontWeight: 900, marginBottom: "3px",
                                    color: active ? C.accent : C.text,
                                }}>
                                    {emoji} {label}
                                </div>
                                <div style={{ fontSize: "10px", color: C.faint }}>{desc}</div>
                            </button>
                        );
                    })}
                </div>

                {/* ── CONTEXTUAL: botones rápidos + input ─────────────────── */}
                {isContextual && (
                    <div style={{
                        animation: "cardIn 0.25s ease forwards",
                        marginBottom: "24px",
                    }}>
                        <p style={{
                            fontSize: "9px", fontWeight: 700, color: C.muted,
                            letterSpacing: "2px", textTransform: "uppercase",
                            marginBottom: "10px"
                        }}>Tu área de interés</p>

                        {/* Grid de 6 botones rápidos */}
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: "8px",
                            marginBottom: "12px",
                        }}>
                            {QUICK_CONTEXTS.map(({ id, emoji, label }) => {
                                const active = quickSelected === id;
                                return (
                                    <button
                                        key={id}
                                        onClick={() => handleQuickSelect(id)}
                                        style={{
                                            background: active
                                                ? "linear-gradient(135deg, rgba(255,237,78,0.18), rgba(255,140,66,0.12))"
                                                : "var(--bg-primary)",
                                            border: active
                                                ? "1.5px solid rgba(255,237,78,0.45)"
                                                : "1.5px solid transparent",
                                            borderRadius: "14px",
                                            padding: "12px 8px",
                                            cursor: "pointer",
                                            fontFamily: "inherit",
                                            transition: "all 0.2s",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            gap: "5px",
                                            boxShadow: active
                                                ? `inset -3px 3px 6px var(--shadow-dark), inset 3px -3px 6px var(--shadow-light), 0 0 12px rgba(255,237,78,0.12)`
                                                : "-3px 3px 6px var(--shadow-dark), 3px -3px 6px var(--shadow-light)",
                                        }}
                                    >
                                        <span style={{ fontSize: "22px", lineHeight: 1 }}>{emoji}</span>
                                        <span style={{
                                            fontSize: "11px", fontWeight: 800,
                                            color: active ? C.accent : C.text,
                                            textAlign: "center", lineHeight: 1.2,
                                        }}>{label}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Separador */}
                        <div style={{
                            display: "flex", alignItems: "center", gap: "10px",
                            marginBottom: "12px",
                        }}>
                            <div style={{ flex: 1, height: "1px", background: C.veil06 }} />
                            <span style={{ fontSize: "10px", color: C.faint, fontWeight: 600 }}>o escribe tu interés</span>
                            <div style={{ flex: 1, height: "1px", background: C.veil06 }} />
                        </div>

                        {/* Input libre */}
                        <input
                            maxLength={LIMITES.contexto}
                            value={customInput}
                            onChange={e => handleInputChange(e.target.value)}
                            placeholder="Ej: Soy chef en un crucero internacional..."
                            style={{
                                width: "100%", background: "var(--bg-secondary)",
                                boxShadow: customInput.length > 0
                                    ? `inset -4px 4px 8px var(--shadow-dark), inset 4px -4px 8px var(--shadow-light), 0 0 0 1.5px rgba(255,237,78,0.35)`
                                    : "inset -4px 4px 8px var(--shadow-dark), inset 4px -4px 8px var(--shadow-light)",
                                borderRadius: "13px", padding: "13px 14px",
                                color: C.text, fontSize: "13px",
                                fontFamily: "inherit", outline: "none",
                                transition: "box-shadow 0.2s",
                                boxSizing: "border-box", border: "none",
                            }}
                        />
                    </div>
                )}

                {/* ── SELECTOR DE NIVEL (aparece al hacer clic en ⚙️) ──────── */}
                <div style={{
                        animation: "cardIn 0.2s ease",
                        marginBottom: "24px",
                        background: "var(--bg-primary)",
                        borderRadius: "16px",
                        padding: "16px 14px",
                        boxShadow: "-6px 6px 12px var(--shadow-dark), 6px -6px 12px var(--shadow-light)",
                    }}>
                        <p style={{
                            fontSize: "9px", fontWeight: 700, color: C.muted,
                            letterSpacing: "2px", textTransform: "uppercase",
                            marginBottom: "12px", textAlign: "center",
                        }}>
                            Selecciona tu nivel
                        </p>
                        <div style={{ display: "grid", gap: "8px" }}>
                            {[
                                { code: "A1", group: "basic" },
                                { code: "B1", group: "intermediate" },
                                { code: "C1", group: "advanced" },
                            ].map(({ code, group }) => {
                                const active = selectedLevel === code;
                                const col = levelColor(group);
                                return (
                                    <button
                                        key={code}
                                        onClick={() => selectLevel(code)}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "12px",
                                            background: active
                                                ? `linear-gradient(135deg, ${alpha(col, 13)}, ${alpha(col, 7)})`
                                                : "var(--bg-secondary)",
                                            border: active
                                                ? `1.5px solid ${alpha(col, 60)}`
                                                : "1.5px solid transparent",
                                            borderRadius: "12px",
                                            padding: "12px 14px",
                                            cursor: "pointer",
                                            fontFamily: "inherit",
                                            transition: "all 0.2s",
                                            boxShadow: active
                                                ? `inset -3px 3px 6px var(--shadow-dark), inset 3px -3px 6px var(--shadow-light), 0 0 14px ${alpha(col, 27)}`
                                                : "-3px 3px 6px var(--shadow-dark), 3px -3px 6px var(--shadow-light)",
                                            color: C.text,
                                            textAlign: "left",

                                        }}
                                    >
                                        <span style={{ fontSize: "18px" }}>
                                            {levelEmoji(group)}
                                        </span>
                                        <div>
                                            <div style={{
                                                fontSize: "13px", fontWeight: 900,
                                                color: active ? col : C.text,
                                            }}>
                                                {levelLabel(group)}
                                            </div>
                                            <div style={{
                                                fontSize: "10px", color: C.faint, marginTop: "2px",
                                            }}>
                                                {group === "basic" ? "Principiante" : group === "intermediate" ? "Intermedio" : "Avanzado"}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                        </div>

                {/* ── CTA ─────────────────────────────────────────────────── */}
                <button
                    onClick={handleLaunch}
                    disabled={!canLaunch}
                    style={{
                        ...btnStyle(C.accentFill, "#000", !canLaunch),
                        width: "100%", fontSize: "15px", padding: "16px",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: "10px",
                    }}
                >
                    {canLaunch ? (
                        <>🚀 Comenzar clase →</>
                    ) : (
                        <>Selecciona tu área de interés</>
                    )}
                </button>

                {/* ── Divisor + Spacy info ─────────────────────────────────── */}
                <div style={{ display: "flex", alignItems: "center", gap: "12px", margin: "20px 0 14px" }}>
                    <div style={{ flex: 1, height: "1px", background: C.veil06 }} />
                    <span style={{ fontSize: "10px", color: C.faint, fontWeight: 600, letterSpacing: "1px", textTransform: "uppercase" }}>
                        ó también
                    </span>
                    <div style={{ flex: 1, height: "1px", background: C.veil06 }} />
                </div>

                {/* Antes aquí se anunciaba que Spacy vive dentro de la clase.
                    Eso ya se entiende al verlo en el mapa, así que el espacio se
                    usa para lo único que el camino con IA no cubre: entrar
                    directo al temario curado, con sus ejercicios completos y sin
                    gastar un solo token. Es la puerta del alumno que agotó sus
                    tokens pero tiene su suscripción al corriente. */}
                <button
                    onClick={onGoClassic}
                    style={{
                        width: "100%", padding: "14px 16px",
                        background: "var(--bg-secondary)",
                        border: "none",
                        borderRadius: "16px",
                        display: "flex", alignItems: "flex-start", gap: "12px",
                        textAlign: "left", fontFamily: "inherit", cursor: "pointer",
                        boxShadow: "-4px 4px 8px var(--shadow-dark), 4px -4px 8px var(--shadow-light)",
                        transition: "transform 0.18s ease",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; }}
                >
                    <span style={{ fontSize: "22px", flexShrink: 0, marginTop: 2 }}>📚</span>
                    <div style={{ flex: 1, whiteSpace: "normal" }}>
                        <div style={{ fontSize: "12px", fontWeight: 800, color: C.text, marginBottom: "3px" }}>
                            Practicar con el temario NSL
                        </div>
                        <div style={{ fontSize: "11px", color: C.faint, lineHeight: 1.5 }}>
                            Los ejercicios curados de esta lección, completos y sin gastar
                            tokens. Útil si ya agotaste los de tu plan y quieres seguir.
                        </div>
                    </div>
                    <span style={{ fontSize: "15px", color: C.faint, alignSelf: "center", flexShrink: 0 }}>→</span>
                </button>

                <p style={{
                    fontSize: "10px", color: C.faint,
                    marginTop: "18px", textAlign: "center", letterSpacing: "0.4px",
                }}>
                    ⚡ Powered by Non-Stop Learning AI
                </p>
            </div>

        </>
    );
}

/* ─── BLOCK OVERVIEW ──────────────────────────────────────────────────────── */
function BlockOverview({ motorInfo, completedBlocks, lessonTitle, onStartBlock, onBack,
                        spacyDone, onStartSpacy, hasSpacy, nivel }) {
    const blocks = motorInfo?.blocks || [];
    const totalBlocks = blocks.length;
    const doneCount = Object.keys(completedBlocks).length;

    // ── LA ARENA NO SE CALIFICA ──────────────────────────────────────────────
    // Es un ejercicio, asi que vale 2 puntos, y el mapa escribia "2/10" — en
    // rojo, porque 2 cae en la banda de suspenso. El bloque estrella del curso
    // aparecia como si el alumno lo hubiera hecho fatal justo despues de
    // felicitarle con un 2/2 y confeti en la pantalla anterior.
    //
    // No se arregla subiendole el valor: la Arena es una conversacion de dos
    // minutos con una persona, y ponerle nota en el mapa la convierte en un
    // ejercicio mas. Lo que importa aqui es que se enfrento a ella. La nota que
    // si tiene sentido —el veredicto— ya se la dan al terminar la mision.
    //
    // Se detecta por `arena_mission` y no por el nombre del bloque porque es la
    // misma cadena con la que el motor decide que tarjeta pintar: una sola
    // fuente de verdad.
    const esBloqueArena = (b) => (b?.exercise_types || []).includes("arena_mission");

    // Y por lo mismo queda fuera de la media: un 2 sobre 10 arrastraba el
    // porcentaje de toda la leccion hacia abajo.
    // `|| 0` en la nota: esta media daba NaN cuando el progreso venía del
    // servidor como número suelto, y NaN se pinta como "NaN%" en la cabecera.
    const conNota = Object.keys(completedBlocks)
        .filter((i) => !esBloqueArena(blocks[Number(i)]));
    const avgScore = conNota.length > 0
        ? Math.round(conNota
            .reduce((s, i) => s + (Number(completedBlocks[i]?.score) || 0), 0)
            / conNota.length * 10)
        : 0;
    const nextBlock = doneCount < totalBlocks ? doneCount : null;

    // El camino incluye la conversación. Mientras Spacy esté pendiente en su
    // posición, ÉL es el siguiente paso — no el bloque.
    //
    // `hasSpacy` es false cuando la lección no trae `conversation_spacy_prompt`.
    // En ese caso el paso no existe: no se anuncia un paso que llevaría a una
    // pantalla vacía, ni se cuenta en el total.
    const spacyPending = hasSpacy && !spacyDone;
    const spacyIsNext =
        spacyPending && (SPACY_POSITION === "open" ? doneCount === 0 : nextBlock === null);
    const pathTotal = totalBlocks + (hasSpacy ? 1 : 0);
    const pathDone = doneCount + (hasSpacy && spacyDone ? 1 : 0);

    /* Tarjeta de Spacy — misma anatomía que la de un bloque, para que el
       alumno la lea como un paso más del camino y no como un desvío. */
    const spacyCard = !hasSpacy ? null : (
        <button
            key="spacy"
            onClick={onStartSpacy}
            className="em-block-item"
            style={{
                background: "var(--bg-primary)",
                borderRadius: "14px", padding: "14px 16px", cursor: "pointer",
                fontFamily: "inherit", transition: "all 0.2s", display: "flex",
                alignItems: "center", gap: "14px", textAlign: "left", width: "100%", border: "none",
                boxShadow: spacyIsNext
                    ? `inset -4px 4px 8px var(--shadow-dark), inset 4px -4px 8px var(--shadow-light), 0 0 12px var(--em-accent-glow)`
                    : "-4px 4px 8px var(--shadow-dark), 4px -4px 8px var(--shadow-light)",
            }}
        >
            <div style={{
                width: "40px", height: "40px", borderRadius: "12px", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px",
                ...cardNeu({ borderRadius: "12px" }),
            }}>
                {spacyDone ? "✅" : "🤖"}
            </div>
            <div style={{ flex: 1 }}>
                <div className="em-block-item-title" style={{
                    fontSize: "12px", fontWeight: 900,
                    color: spacyIsNext ? C.accent : spacyDone ? C.green : C.text,
                    marginBottom: "2px",
                }}>
                    {SPACY_POSITION === "open" ? "Apertura" : "Cierre"}: Conversación con Spacy
                </div>
                <div style={{ fontSize: "10px", color: C.faint }}>
                    {T(nivel, "spacyPasos")}
                </div>
            </div>
            {spacyDone
                ? <span style={{ fontSize: "12px", color: C.green, fontWeight: 800, flexShrink: 0 }}>hecho</span>
                : <span style={{ fontSize: "16px", color: spacyIsNext ? C.accent : C.faint, flexShrink: 0 }}>→</span>}
        </button>
    );

    return (
        <div className="em-block-overview" style={{ padding: "20px 20px 48px", maxWidth: "clamp(520px, 60vw, 720px)", margin: "0 auto", position: "relative", zIndex: 1 }}>
            {/* Live badge */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                <div style={{
                    width: "6px", height: "6px", background: C.green, borderRadius: "50%",
                    animation: "livePing 1.5s ease-in-out infinite"
                }} />
                <span style={{ fontSize: "9px", fontWeight: 700, color: C.green, letterSpacing: "2px", textTransform: "uppercase" }}>
                    {T(nivel, "modoIA")}
                </span>
            </div>
            <h2 className="em-overview-title" style={{ fontSize: "22px", fontWeight: 900, color: C.text, marginBottom: "4px" }}>{lessonTitle}</h2>
            <p style={{ fontSize: "12px", color: C.muted, marginBottom: "20px", fontStyle: "italic" }}>
                {pathDone} / {pathTotal} {T(nivel, "pasosHechos")}
            </p>

            {/* Stats */}
            <div className="em-stats-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", marginBottom: "22px" }}>
                {[
                    ["📚", pathTotal, T(nivel, "pasos")],
                    ["✅", pathDone, T(nivel, "completados")],
                    ["⭐", avgScore > 0 ? `${avgScore}%` : "—", T(nivel, "promedio")],
                ].map(([icon, val, lbl]) => (
                    <div key={lbl} style={{
                        ...cardNeu({ borderRadius: "12px" }),
                        padding: "12px 6px", textAlign: "center"
                    }}>
                        <div style={{ fontSize: "16px", marginBottom: "2px" }}>{icon}</div>
                        <div style={{ fontSize: "18px", fontWeight: 900, color: C.accent }}>{val}</div>
                        <div style={{ fontSize: "9px", color: C.muted, marginTop: "2px" }}>{lbl}</div>
                    </div>
                ))}
            </div>

            {/* CTA — un solo botón que siempre sabe cuál es el siguiente paso */}
            {spacyIsNext && (
                <button onClick={onStartSpacy}
                    style={{
                        ...btnStyle(C.accentFill, "#000"), width: "100%", fontSize: "15px", padding: "14px", marginBottom: "20px",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: "10px"
                    }}>
                    <span>🤖 {SPACY_POSITION === "open" ? "Empezar hablando con Spacy" : "Cerrar hablando con Spacy"}</span>
                    <span style={{ fontSize: "18px" }}>→</span>
                </button>
            )}
            {!spacyIsNext && nextBlock !== null && (
                <button onClick={() => onStartBlock(nextBlock)}
                    style={{
                        ...btnStyle(C.accentFill, "#000"), width: "100%", fontSize: "15px", padding: "14px", marginBottom: "20px",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: "10px"
                    }}>
                    <span>⚡ {doneCount === 0 ? T(nivel, "empezar") : T(nivel, "continuar")} {blocks[nextBlock]?.label}</span>
                    <span style={{ fontSize: "18px" }}>→</span>
                </button>
            )}
            {!spacyIsNext && nextBlock === null && spacyPending && (
                <button onClick={onStartSpacy}
                    style={{
                        ...btnStyle(C.accentFill, "#000"), width: "100%", fontSize: "15px", padding: "14px", marginBottom: "20px",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: "10px"
                    }}>
                    <span>🤖 Último paso: habla con Spacy</span>
                    <span style={{ fontSize: "18px" }}>→</span>
                </button>
            )}
            {nextBlock === null && !spacyPending && (
                <div style={{
                    ...insetNeu(), borderRadius: "14px",
                    padding: "16px", marginBottom: "20px", textAlign: "center",
                    boxShadow: `inset -4px 4px 8px var(--shadow-dark), inset 4px -4px 8px var(--shadow-light), 0 0 12px ${alpha(C.green, 20)}`
                }}>
                    <div style={{ fontSize: "32px", marginBottom: "8px" }}>🏆</div>
                    <p style={{ fontSize: "14px", fontWeight: 800, color: C.green }}>¡Clase completada!</p>
                    <p style={{ fontSize: "12px", color: C.muted, marginTop: "4px" }}>
                        Practicaste, escribiste y hablaste. Meta del día cerrada.
                    </p>
                </div>
            )}

            {/* Camino de la lección — la conversación es un paso más */}
            <div style={{ display: "grid", gap: "8px" }}>
                {SPACY_POSITION === "open" && spacyCard}
                {blocks.map((block, i) => {
                    const prog = completedBlocks[i];
                    const isNext = i === nextBlock;
                    // `!!prog` daba falso con nota CERO y el bloque volvia a
                    // aparecer sin hacer. Se mira si EXISTE, no si vale.
                    const isDone = prog !== undefined && prog !== null;
                    const isLocked = !isDone && i > nextBlock;
                    const score = prog?.score;
                    // El 10/10 tiene su propio color. Antes 8, 9 y 10 salian
                    // todos en verde y el bloque perfecto no se distinguia de
                    // uno bueno — que es justo lo que hay que poder ver de un
                    // vistazo al mirar el mapa.
                    const esArena = esBloqueArena(block);
                    const esPerfecto = !esArena && score === 10;
                    const scoreColor = esPerfecto ? C.accent
                        : score >= 8 ? C.green
                            : score >= 6 ? C.orange : C.red;
                    return (
                        <button key={i} onClick={() => !isLocked && onStartBlock(i)}
                            disabled={isLocked}
                            className="em-block-item"
                            style={{
                                background: "var(--bg-primary)",
                                borderRadius: "14px", padding: "14px 16px", cursor: isLocked ? "default" : "pointer",
                                fontFamily: "inherit", transition: "all 0.2s", display: "flex",
                                alignItems: "center", gap: "14px", opacity: isLocked ? 0.4 : 1,
                                textAlign: "left", width: "100%", border: "none",
                                boxShadow: isNext
                                    ? `inset -4px 4px 8px var(--shadow-dark), inset 4px -4px 8px var(--shadow-light), 0 0 12px var(--em-accent-glow)`
                                    : isDone
                                        ? "-4px 4px 8px var(--shadow-dark), 4px -4px 8px var(--shadow-light)"
                                        : "-3px 3px 6px var(--shadow-dark), 3px -3px 6px var(--shadow-light)"
                            }}>
                            <div style={{
                                width: "40px", height: "40px", borderRadius: "12px", flexShrink: 0,
                                display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px",
                                ...cardNeu({ borderRadius: "12px" })
                            }}>
                                {/* El trofeo no es adorno: si lo perfecto se
                                    distinguiera SOLO por el color, un alumno
                                    daltonico no lo veria nunca. */}
                                {esPerfecto ? "🏆" : isDone ? "✅" : isNext ? "⚡" : BLOCK_ICONS[i] || "📌"}
                            </div>
                            <div style={{ flex: 1 }}>
                                <div className="em-block-item-title" style={{
                                    fontSize: "12px", fontWeight: 900, color: isNext ? C.accent : isDone ? C.green : C.text,
                                    marginBottom: "2px"
                                }}>
                                    Bloque {i + 1}: {block.label}
                                </div>
                                <div style={{ fontSize: "10px", color: C.faint }}>
                                    {block.exercise_types?.join(" · ") || "5 ejercicios"}
                                </div>
                            </div>
                            {/* La Arena se marca igual que la Apertura: la
                                misma palabra, en el mismo sitio y del mismo
                                verde. Las dos son conversaciones, y ninguna se
                                puntua en el mapa. */}
                            {isDone && esArena && (
                                <span style={{ fontSize: "12px", color: C.green, fontWeight: 800, flexShrink: 0 }}>
                                    hecho
                                </span>
                            )}
                            {isDone && !esArena && (
                                <div style={{ textAlign: "right", flexShrink: 0 }}>
                                    <div style={{ fontSize: "16px", fontWeight: 900, color: scoreColor }}>{score}/10</div>
                                    <div style={{ fontSize: "9px", color: C.faint }}>{T(nivel, "puntos")}</div>
                                </div>
                            )}
                            {isNext && <span style={{ fontSize: "16px", color: C.accent, flexShrink: 0 }}>→</span>}
                            {isLocked && <span style={{ fontSize: "14px", color: C.faint, flexShrink: 0 }}>🔒</span>}
                        </button>
                    );
                })}
                {SPACY_POSITION === "close" && spacyCard}
            </div>

            <button onClick={onBack} style={{
                background: "var(--bg-secondary)", borderRadius: "12px", padding: "10px", width: "100%", marginTop: "16px",
                fontSize: "12px", color: C.muted, cursor: "pointer", fontFamily: "inherit", fontWeight: 700,
                border: "none", boxShadow: "inset -3px 3px 6px var(--shadow-dark), inset 3px -3px 6px var(--shadow-light)"
            }}>
                ← Cambiar configuración
            </button>
        </div>
    );
}

/* ─── BLOCK RESULT ────────────────────────────────────────────────────────── */
function BlockResult({ blockLabel, score, maxScore, record, sinNota, isLastBlock, onNextBlock, onOverview, nivel }) {
    const pct = Math.round((score / maxScore) * 100);
    const grade = pct >= 90 ? "S" : pct >= 80 ? "A" : pct >= 70 ? "B" : pct >= 60 ? "C" : "D";
    const gradeColor = { S: C.accent, A: C.green, B: C.blue, C: C.orange, D: C.red }[grade];

    // ── SOLO CON EL BLOQUE PERFECTO ──────────────────────────────────────────
    // No `pct >= 90`: exactamente todo. Un 9/10 ya saca la S y ya se celebra
    // con el trofeo y la nota; si el confeti tambien saliera ahi, dejaria de
    // significar nada. Lo que hace que valga es que no salga casi nunca.
    //
    // `maxScore > 0` porque un bloque sin puntos daria 0 === 0 y lanzaria
    // confeti por no haber hecho nada.
    // La Arena no se puntua. Sin esto, terminarla enseñaba "2/2 · S · 100%" con
    // confeti —vale 2 puntos porque es un ejercicio— justo despues de su
    // veredicto de verdad. Una nota inventada encima de una real.
    const perfecto = !sinNota && maxScore > 0 && score === maxScore;

    // ── EL LOGRO SIGUE AHI, Y HAY QUE DECIRLO ────────────────────────────────
    // Un 10/10 conseguido no se pierde. Pero si el alumno repite el bloque para
    // reafirmar y esta vez saca 6, esta pantalla le enseña un 6 mientras el
    // mapa sigue con su 10 — y sin explicacion eso parece un error del
    // producto, o peor, le deja creyendo que acaba de perder su logro.
    //
    // Un renglon lo resuelve, y es justo donde se construye la confianza: el
    // alumno vuelve a entrar sabiendo que practicar no le puede costar nada.
    const conservaRecord = typeof record === "number" && record > score;

    return (
        <div style={{
            padding: "20px 20px 48px", maxWidth: "clamp(520px, 60vw, 720px)", margin: "0 auto",
            position: "relative", zIndex: 1, textAlign: "center"
        }}>
            <Confetti active={perfecto} />
            <div style={{ fontSize: "56px", marginBottom: "10px", animation: "bounceIn 0.6s ease" }}>
                {sinNota ? "🎙️" : pct >= 80 ? "🏆" : pct >= 60 ? "⭐" : "💪"}
            </div>
            <h2 style={{ fontSize: "24px", fontWeight: 900, color: C.text, marginBottom: "4px" }}>
                {T(nivel, "bloqueHecho")}
            </h2>
            <p style={{ color: C.muted, fontSize: "12px", fontStyle: "italic", marginBottom: "20px" }}>
                {blockLabel}
            </p>
            {!sinNota && (
            <div className="em-result-score-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "20px" }}>
                <div style={{ ...cardNeu({ borderRadius: "16px" }), padding: "20px" }}>
                    <div style={{ fontSize: "36px", fontWeight: 900, color: C.accent }}>{score}/{maxScore}</div>
                    <div style={{ fontSize: "10px", color: C.muted, marginTop: "3px" }}>{T(nivel, "PUNTOS")}</div>
                </div>
                <div style={{ ...cardNeu({ borderRadius: "16px" }), padding: "20px" }}>
                    <div style={{ fontSize: "36px", fontWeight: 900, color: gradeColor }}>{grade}</div>
                    <div style={{ fontSize: "10px", color: C.muted, marginTop: "3px" }}>{pct}%</div>
                </div>
            </div>
            )}
            {conservaRecord && (
                <div style={{
                    ...insetNeu(), borderRadius: "13px",
                    padding: "12px 14px", marginBottom: "12px",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    gap: "8px", flexWrap: "wrap",
                }}>
                    <span style={{ fontSize: "16px" }} aria-hidden="true">🏆</span>
                    <span style={{ fontSize: "12.5px", color: C.text, fontWeight: 700 }}>
                        Tu {record}/{maxScore} sigue guardado.
                    </span>
                    <span style={{ fontSize: "12.5px", color: C.muted }}>
                        Practicar nunca te baja la nota.
                    </span>
                </div>
            )}
            <div style={{
                ...insetNeu(), borderRadius: "13px",
                padding: "14px", marginBottom: "24px"
            }}>
                <p style={{ fontSize: "13px", color: C.muted, lineHeight: 1.65 }}>
                    {sinNota ? T(nivel, "cierreArena") :
                        pct >= 80 ? T(nivel, "cierreAlto") :
                            pct >= 60 ? T(nivel, "cierreMedio") :
                                T(nivel, "cierreBajo")}
                </p>
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
                <button onClick={onOverview} style={{
                    background: "var(--bg-secondary)", borderRadius: "14px", flex: 1, fontSize: "13px",
                    padding: "13px", color: C.text, cursor: "pointer", fontFamily: "inherit", fontWeight: 700,
                    border: "none", boxShadow: "-4px 4px 8px var(--shadow-dark), 4px -4px 8px var(--shadow-light)"
                }}>
                    {T(nivel, "verMapa")}
                </button>
                {!isLastBlock && (
                    <button onClick={onNextBlock} style={{ ...btnStyle(C.accentFill, "#000"), flex: 2, fontSize: "14px", padding: "13px" }}>
                        {T(nivel, "siguiente")}
                    </button>
                )}
                {isLastBlock && (
                    <button onClick={onOverview} style={{ ...btnStyle(C.green, "#000"), flex: 2, fontSize: "14px", padding: "13px" }}>
                        🏆 Ver resultados finales
                    </button>
                )}
            </div>
        </div>
    );
}

/* ══════════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT — EngineMode
   ══════════════════════════════════════════════════════════════════════════════ */
// `spacyDone` y `onOpenSpacy` los gobierna LessonPage, que es quien monta a
// Spacy. Aquí sólo se pintan en el mapa como un paso más del camino.
export default function EngineMode({
    lessonId, onExit, onMotorUnavailable,
    spacyDone = false, onOpenSpacy, hasSpacy = true, onGoClassic, resumeSignal = 0,
}) {
    /* ── Screens ──────────────────────────────────────────────────────────── */
    // Si esta lección ya tiene configuración guardada, se entra directo al
    // mapa. Preguntar dos veces lo mismo es lo que rompía la sensación de
    // avance lineal.
    const [screen, setScreen] = useState(() => (readConfig(lessonId) ? "overview" : "setup"));

    /* ── Motor data ───────────────────────────────────────────────────────── */
    const [motorInfo, setMotorInfo] = useState(null);
    const [completedBlocks, setCompletedBlocks] = useState({});
    const [lessonTitle, setLessonTitle] = useState("Lección");
    const [lessonLevel, setLessonLevel] = useState("A1");

    /* ── Session config ───────────────────────────────────────────────────── */
    const [sessionConfig, setSessionConfig] = useState(() => readConfig(lessonId));

    /* ── Block game state ─────────────────────────────────────────────────── */
    const [currentBlock, setCurrentBlock] = useState(0);
    const [blockData, setBlockData] = useState(null);
    const [exercisePhase, setExercisePhase] = useState("grammar");
    const [exerciseIdx, setExerciseIdx] = useState(0);

    // ── LA FASE DE VERDAD ────────────────────────────────────────────────────
    // `exercisePhase` arranca SIEMPRE en "grammar", antes de saber si el bloque
    // trae nota gramatical. Cuando no la trae, `EngineGrammarCard` leía
    // `grammarNote.structure_formula` sobre un null y se llevaba por delante la
    // pantalla entera — blanca, sin nada que le dijera al alumno qué pasó.
    //
    // Le pasaba a cualquier bloque cuya nota viniera vacía; la Arena solo lo
    // hizo seguro, porque no tiene nota por definición: allí no hay gramática
    // que explicar, hay alguien con un problema.
    //
    // Se deriva en vez de corregir el estado para que no haya un instante entre
    // "llegaron los datos" y "se arregló la fase" en el que se pinte el fallo.
    const faseVisible = (exercisePhase === "grammar" && !blockData?.grammar_note)
        ? "exercises"
        : exercisePhase;
    const [blockScore, setBlockScore] = useState(0);
    // El score que de verdad se guardo. Ver el comentario en `finishBlock`.
    const [scoreGuardado, setScoreGuardado] = useState(null);
    // Lo que queda guardado en el mapa despues de este intento. Puede ser
    // MAYOR que lo de este intento: un 10/10 ya conseguido no se pierde.
    const [recordBloque, setRecordBloque] = useState(null);
    const [blockCorrect, setBlockCorrect] = useState(0);
    const [animKey, setAnimKey] = useState(0);
    const [blockStartTime, setBlockStartTime] = useState(null);

    /* ── HUD ──────────────────────────────────────────────────────────────── */
    const [fireEnergy, setFireEnergy] = useState(70);
    const [fireShake, setFireShake] = useState(false);
    const [xp, setXp] = useState(0);
    const [level, setLevel] = useState(1);
    const [streak, setStreak] = useState(0);
    const [totalAnswered, setTotalAnswered] = useState(0);

    /* ── Loading ──────────────────────────────────────────────────────────── */
    const [isLoadingDone, setIsLoadingDone] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);

    /* ── Paywall ──────────────────────────────────────────────────────────── */
    const [showPaywall, setShowPaywall] = useState(false);
    const [paywallReason, setPaywallReason] = useState("tokens");

    /* ── Token Info ───────────────────────────────────────────────────────── */
    const [tokenInfo, setTokenInfo] = useState(null);   // { tokens_remaining, token_limit }

    /* ── Resincronizar al cambiar de lección ──────────────────────────────
       El inicializador de useState sólo corre en el primer montaje. Si el
       alumno pasa de /lesson/1 a /lesson/2 sin desmontar este componente, se
       quedaría con la configuración de la lección anterior. */
    useEffect(() => {
        const guardada = readConfig(lessonId);
        setSessionConfig(guardada);
        setScreen(guardada ? "overview" : "setup");
    }, [lessonId]);

    /* ── Fetch motor info ─────────────────────────────────────────────────── */
    useEffect(() => {
        const fetchMotorInfo = async () => {
            try {
                const cfgGuardada = readConfig(lessonId);
                const nivelElegido = cfgGuardada?.level_code || "";
                const res = await fetch(
                    `/api/engines/motor-info/${lessonId}` +
                    (nivelElegido ? `?nivel=${encodeURIComponent(nivelElegido)}` : ""), {
                    headers: { "Authorization": `Bearer ${getToken()}` },
                });
                if (!res.ok) throw new Error("Motor info error");
                const data = await res.json();
                setMotorInfo(data);
                setLessonTitle(data.lesson_title || "Lección");
                setLessonLevel(data.lesson_level || data.motor_metadata?.level || "A1");
                setCompletedBlocks(normalizarProgreso(data.student_progress));
            } catch (e) {
                // Una lección sin motor asignado devuelve 400 aquí. Antes eso
                // dejaba la pantalla en "Cargando motor..." para siempre: un
                // callejón sin salida silencioso. Ahora se avisa hacia arriba
                // para que la página sirva el temario curado en su lugar.
                console.error("[EngineMode] Error fetching motor info:", e);
                onMotorUnavailable?.();
            }
        };
        fetchMotorInfo();
    }, [lessonId]);

    /* ── Fetch tokens ─────────────────────────────────────────────────────── */
    useEffect(() => {
        const fetchTokens = async () => {
            try {
                const res = await fetch("/api/subscription/status", {
                    headers: { "Authorization": `Bearer ${getToken()}` },
                });
                if (!res.ok) return;
                const data = await res.json();
                setTokenInfo({
                    remaining: data.tokens_remaining ?? 0,
                    limit: data.token_limit ?? 0,
                });
            } catch { /* silencioso — no es crítico */ }
        };
        fetchTokens();
    }, []);

    /* ── Autoguardado de sesión en sessionStorage ─────────────────────────── */
    useEffect(() => {
        if (screen !== "block" || !blockData) return;
        try {
            sessionStorage.setItem(
                getSessionKey(lessonId, currentBlock),
                JSON.stringify({
                    streak, xp, level, fireEnergy,
                    exerciseIdx, blockScore, blockCorrect,
                    totalAnswered, exercisePhase,
                })
            );
        } catch { /* sessionStorage lleno o bloqueado — ignorar */ }
    }, [streak, xp, level, fireEnergy, exerciseIdx, blockScore, screen]);

    /* ── Setup → overview ─────────────────────────────────────────────────── */
    const handleSetupDone = config => {
        setSessionConfig(config);
        try { sessionStorage.setItem(getConfigKey(lessonId), JSON.stringify(config)); }
        catch { /* almacenamiento bloqueado — se pierde al recargar, nada más */ }
        setScreen("overview");
    };

    /* ── Spacy ────────────────────────────────────────────────────────────────
       No se abre aquí: se pide hacia arriba. LessonPage monta el mismo
       SpeakingExercise del Modo Clásico, fuera de este contenedor. */
    const startSpacy = () => onOpenSpacy?.();

    /* ── EngineLoader onComplete ──────────────────────────────────────────── */
    const handleLoaderComplete = () => {
        setScreen("block");
    };

    /* ── Start a block ────────────────────────────────────────────────────── */
    const startBlock = async (blockNum) => {
        setCurrentBlock(blockNum);
        setBlockData(null);
        setExercisePhase("grammar");
        setExerciseIdx(0);
        fxStart();
        setBlockScore(0);
        // Sin esto, la pantalla de resultado del bloque nuevo enseñaria el
        // numero del bloque anterior hasta que terminara.
        setScoreGuardado(null);
        setRecordBloque(null);
        setBlockCorrect(0);
        setIsLoadingDone(false);
        setBlockStartTime(Date.now());
        setAnimKey(k => k + 1);
        setScreen("loading");

        // ── AbortController: timeout de 20s ──────────────────────────────────
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 20000);

        let res;
        try {
            res = await fetch("/api/engines/generate-block", {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${getToken()}` },
                body: JSON.stringify({
                    lesson_id: lessonId,
                    block_num: blockNum,
                    mode: sessionConfig?.mode || "academic",
                    context: sessionConfig?.context || "English learner",
                    objective: sessionConfig?.objective || lessonTitle,
                    level_code: sessionConfig?.level_code || lessonLevel,
                }),
                signal: controller.signal,   // ← conecta el timeout
            });
            clearTimeout(timeoutId);         // ← limpiar si responde a tiempo

            // ── FASE 1: Blindaje del Token Guardian ──────────────────────────
            if (res.status === 429 || res.status === 402) {
                const errData = await res.json().catch(() => ({}));
                const status = errData?.detail?.subscription_status || "tokens";
                setPaywallReason(status);
                setScreen("overview");
                setShowPaywall(true);
                return;
            }

            if (!res.ok) throw new Error(`Generate block error: ${res.status}`);

            const data = await res.json();
            setBlockData(data);

            // Restaurar sesión interrumpida si existe
            try {
                const saved = sessionStorage.getItem(getSessionKey(lessonId, blockNum));
                if (saved) {
                    const s = JSON.parse(saved);
                    setStreak(s.streak ?? 0);
                    setXp(s.xp ?? 0);
                    setLevel(s.level ?? 1);
                    setFireEnergy(s.fireEnergy ?? 70);
                    setExerciseIdx(s.exerciseIdx ?? 0);
                    setBlockScore(s.blockScore ?? 0);
                    setBlockCorrect(s.blockCorrect ?? 0);
                    setTotalAnswered(s.totalAnswered ?? 0);
                    setExercisePhase(s.exercisePhase ?? "grammar");
                    console.log(`[EngineMode] Sesión restaurada: bloque ${blockNum}`);
                }
            } catch { /* sessionStorage bloqueado — continuar desde cero */ }

            setIsLoadingDone(true);

        } catch (e) {
            clearTimeout(timeoutId);

            // ── Timeout de red: NO gastar fallback, avisar al usuario ──────────
            if (e.name === "AbortError") {
                console.warn("[EngineMode] Timeout: el servidor tardó más de 20s.");
                setBlockData(null);
                setScreen("overview");
                // Reutiliza el paywall modal con un mensaje distinto
                setPaywallReason("timeout");
                setShowPaywall(true);
                return;
            }

            // ── Error de red real (500, offline) → usar fallback ───────────────
            console.error("[EngineMode] Error de red generando bloque:", e);
            setBlockData({
                block_label: "Práctica General",
                block_name: "practice",
                is_last_block: blockNum >= (motorInfo?.total_blocks || 1) - 1,
                grammar_note: {
                    text: "Practica los conceptos de esta lección con atención a la estructura y el uso correcto.",
                    structure_formula: "Subject + Verb + Complement",
                    quick_tip: "Lee cada ejercicio con calma antes de responder.",
                },
                exercises: [
                    {
                        type: "quiz",
                        question: "Which sentence is grammatically correct?",
                        options: ["She works here.", "She work here.", "She working here.", "Her works here."],
                        answer: "She works here.",
                        explanation: "Tercera persona singular lleva -s en el verbo.",
                    },
                ],
            });
            setIsLoadingDone(true);
        }
    };

    /* ── Volver de Spacy con "Continuar" ──────────────────────────────────
       Al terminar un bloque, el motor ofrece "siguiente bloque" o "ver mapa".
       Spacy tenía que comportarse igual: "Continuar" entra directo al
       siguiente bloque pendiente, y quien quiera el mapa tiene su propio
       botón. Dejar al alumno en el mapa después de hablar rompía el ritmo. */
    const primeraSenal = useRef(true);
    useEffect(() => {
        if (primeraSenal.current) { primeraSenal.current = false; return; }
        if (!resumeSignal) return;
        const total = motorInfo?.total_blocks || 0;
        const hechos = Object.keys(completedBlocks).length;
        if (hechos < total) startBlock(hechos);
        else setScreen("overview");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resumeSignal]);

    /* ── Exercise Regeneration (Fix AI Errors) ────────────────────────────── */
    const handleRegenerateExercise = async () => {
        if (!blockData || !blockData.exercises) return;
        const currentExercise = blockData.exercises[exerciseIdx];
        if (!currentExercise) return;

        setIsRegenerating(true);
        try {
            const res = await fetch("/api/engines/regenerate-exercise", {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${getToken()}` },
                body: JSON.stringify({
                    lesson_id: lessonId,
                    exercise_type: currentExercise.type,
                    mode: sessionConfig?.mode || "academic",
                    context: sessionConfig?.context || "English learner",
                    objective: sessionConfig?.objective || lessonTitle,
                    level_code: sessionConfig?.level_code || lessonLevel,
                    error_feedback: "El ejercicio anterior estaba roto, repitió respuestas o no tenía sentido. Genera uno completamente nuevo y correcto.",
                }),
            });

            if (!res.ok) throw new Error("Failed to regenerate");

            const data = await res.json();
            if (data.exercise) {
                setBlockData(prev => {
                    const newExercises = [...prev.exercises];
                    // Keep the same ID and points for tracking
                    data.exercise.id = currentExercise.id;
                    data.exercise.points = currentExercise.points || 2;
                    newExercises[exerciseIdx] = data.exercise;
                    return { ...prev, exercises: newExercises };
                });
                setAnimKey(k => k + 1); // Trigger animation to show it refreshed
            }
        } catch (e) {
            console.error("Error regenerating exercise:", e);
            alert("No se pudo regenerar el ejercicio. Por favor, intenta de nuevo.");
        } finally {
            setIsRegenerating(false);
        }
    };

    /* ── Exercise callbacks ───────────────────────────────────────────────── */
    const handleExerciseCorrect = () => {
        const gained = 10 + streak * 2;
        setXp(x => { const n = x + gained; if (n >= 100) { setLevel(l => l + 1); return n - 100; } return n; });
        setStreak(s => s + 1);
        setFireEnergy(e => Math.min(100, e + 12));
        // Con racha suena el combo, que sube de tono; sin ella, el acierto
        // limpio. La recompensa deja de ser plana, que es justo lo que hace que
        // encadenar aciertos apetezca.
        if (streak >= 2) fxCombo(streak); else fxHit();
        setBlockScore(s => s + 2);
        setBlockCorrect(c => c + 1);
        setTotalAnswered(t => t + 1);
        advanceExercise(2);
    };

    const handleExerciseWrong = () => {
        fxMiss();
        setFireEnergy(e => Math.max(0, e - 10));
        setFireShake(true); setTimeout(() => setFireShake(false), 400);
        setStreak(0);
        setTotalAnswered(t => t + 1);
        advanceExercise(0);
    };

    // ── LOS PUNTOS DEL ULTIMO EJERCICIO VIAJAN A MANO, Y HAY QUE HACERLO ─────
    // `setBlockScore(s => s + 2)` se ENCOLA. Si el ejercicio que se acaba de
    // acertar era el ultimo, `finishBlock` corre en el mismo tick y lee
    // `blockScore` del closure — el valor de ANTES de esos dos puntos.
    //
    // Resultado, medido en pantalla por un alumno: cinco aciertos de cinco
    // mostraban 10/10 con confeti —porque BlockResult se pinta despues, con el
    // estado ya actualizado— y se GUARDABAN 8. El mapa decia 8/10.
    //
    // Y no era cosa del bloque perfecto: CUALQUIER alumno que acertase el
    // ultimo ejercicio llevaba perdiendo 2 puntos desde siempre. Por eso nunca
    // se habia registrado un 10/10 en la base.
    const advanceExercise = (puntosDeEsteEjercicio) => {
        // `onSkip` pasa esta funcion directa a las tarjetas, asi que aqui puede
        // llegar un evento del navegador en vez de un numero.
        const puntos = typeof puntosDeEsteEjercicio === "number" ? puntosDeEsteEjercicio : 0;
        const exercises = blockData?.exercises || [];
        if (exerciseIdx < exercises.length - 1) {
            setAnimKey(k => k + 1);
            setExerciseIdx(i => i + 1);
        } else {
            finishBlock(puntos);
        }
    };

    const finishBlock = async (puntosDeEsteEjercicio = 0) => {
        const timeSeconds = blockStartTime ? Math.round((Date.now() - blockStartTime) / 1000) : 0;
        const finalScore = Math.min(blockScore + puntosDeEsteEjercicio, 10);
        // Se guarda en estado para que la pantalla de resultado enseñe
        // EXACTAMENTE el numero que se mando al servidor. Antes eran dos
        // caminos distintos hacia el mismo numero, y por eso pudieron
        // discrepar sin que nadie lo notara.
        setScoreGuardado(finalScore);

        // ── EL SERVIDOR DECIDE, LA PANTALLA REFLEJA ─────────────────────────
        // Un 10/10 ya conseguido no se pierde: si el alumno repite y saca 6, el
        // servidor devuelve 10 y en el mapa siguen los 10. Aqui se ponia
        // `finalScore` a pelo, y eso habria pintado un 6 sobre un 10 que en la
        // base seguia intacto — el mismo tipo de divergencia entre pantalla y
        // servidor que hizo que un alumno viera 10/10 y su mapa dijera 8/10.
        let efectivo = finalScore;
        try {
            const res = await fetch("/api/engines/save-progress", {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${getToken()}` },
                body: JSON.stringify({
                    lesson_id: lessonId,
                    block_num: currentBlock,
                    block_name: blockData?.block_name || "block",
                    score: finalScore,
                    time_seconds: timeSeconds,
                }),
            });
            if (res.ok) {
                const d = await res.json();
                if (typeof d?.score === "number") efectivo = d.score;
            }
            setCompletedBlocks(prev => ({ ...prev, [currentBlock]: { score: efectivo, block_name: blockData?.block_name } }));
        } catch (e) {
            console.error("[EngineMode] Save progress error:", e);
        }
        setRecordBloque(efectivo);

        // Limpiar sesión guardada — bloque completado correctamente
        try {
            sessionStorage.removeItem(getSessionKey(lessonId, currentBlock));
        } catch { /* ignorar */ }

        // ── NUEVO: actualizar TokenBadge con el gasto real ────────────────
        try {
            const tokenRes = await fetch("/api/subscription/status", {
                headers: { "Authorization": `Bearer ${getToken()}` },
            });
            if (tokenRes.ok) {
                const tokenData = await tokenRes.json();
                setTokenInfo({
                    remaining: tokenData.tokens_remaining ?? 0,
                    limit: tokenData.token_limit ?? 0,
                });
            }
        } catch { /* silencioso — no es crítico */ }

        // El bloque perfecto suena DISTINTO. Es el mismo criterio que el
        // confeti: si sonara igual que un 7/10, la señal no diria nada. Lo que
        // hace que valga es que casi nunca se oye.
        if (finalScore >= 10) fxRecord(); else fxFinish();

        setScreen("block_result");
    };

    /* ── Current exercise ─────────────────────────────────────────────────── */
    const currentExercise = (blockData?.exercises || [])[exerciseIdx];
    const totalExercises = (blockData?.exercises || []).length;

    // La Arena no lleva el HUD de ejercicios. Barra de fuego, puntos de
    // progreso y un marcador de "0/2 pts" alrededor de una conversación de dos
    // minutos la convierten en un ejercicio más, que es exactamente lo que no
    // es. El bloque ocupa la pantalla y se explica solo.
    const esArena = currentExercise?.type === "arena_mission";

    // El nivel que gobierna el idioma de la interfaz. Sale de lo que el alumno
    // eligió en la configuración de la clase; si falta, cae al de la lección.
    const nivelUI = sessionConfig?.level_code || lessonLevel || "A1";

    const renderExerciseCard = (ex) => {
        if (!ex) return null;
        // El nivel viaja a TODAS las tarjetas: es lo que decide si los
        // rótulos van en español (solo A1) o en inglés (A2 en adelante).
        const props = {
            exercise: ex, onCorrect: handleExerciseCorrect, onWrong: handleExerciseWrong,
            onSkip: () => advanceExercise(0), nivel: nivelUI
        };
        switch (ex.type) {
            case "quiz": return <QuizCard {...props} />;
            case "fill_blank": return <FillBlankCard {...props} />;
            case "word_order": return <WordOrderCard {...props} />;
            case "error_detect": return <ErrorDetectCard {...props} />;
            case "matching": return <MatchingCard exercise={ex} onCorrect={handleExerciseCorrect} />;
            case "listening": return <ListeningCard {...props} />;
            // El de pronunciacion tambien pasa al motor nuevo: el viejo usaba
            // SpeechRecognition del navegador y se colgaba en "Escuchando..."
            // sin salida posible (no habia manejador de onend).
            case "pronunciation": return <PronunciationScoredCard {...props} />;
            // ── El Laboratorio de Voz ──────────────────────────────────────
            // Se puntuan con el oido de Deepgram contra un texto conocido, no
            // con SpeechRecognition del navegador comparando cadenas.
            case "read_aloud": return <ReadAloudCard {...props} nivel={lessonLevel || "B1"} />;
            case "speak_score": return <SpeakScoreCard {...props} />;
            case "shadowing": return <ShadowingCard {...props} />;
            case "minimal_pair": return <MinimalPairCard {...props} />;
            case "reading": return <ReadingCard {...props} />;
            case "writing": return <WritingCard {...props} />;
            // ── El bloque 10 ───────────────────────────────────────────────
            // No es una tarjeta más: ocupa la pantalla, abre micrófono y
            // altavoz y dura dos minutos. Recibe el contexto que el alumno
            // dio en el setup para que el personaje sea de SU trabajo.
            //
            // La semilla es la lección: quien repite el bloque se encuentra el
            // mismo escenario, que es lo que espera y lo que permite comparar
            // su inmersión de una vez a la siguiente.
            case "arena_mission": return (
                <ArenaBlock
                    {...props}
                    nivel={sessionConfig?.level_code || lessonLevel || "B1"}
                    contexto={sessionConfig?.context || ""}
                    objetivo={sessionConfig?.objective || lessonTitle || ""}
                    semilla={`leccion-${lessonId}`}
                    modoClase={sessionConfig?.mode || "contextual"}
                />
            );
            default: return (
                <div style={{ textAlign: "center", padding: "20px", color: C.muted, fontSize: "13px" }}>
                    Tipo de ejercicio: <strong style={{ color: C.accent }}>{ex.type}</strong>
                    <br /><button onClick={handleExerciseCorrect} style={{
                        ...btnStyle(C.accentFill, "#000"),
                        marginTop: "14px", padding: "10px 20px", fontSize: "13px"
                    }}>Continuar →</button>
                </div>
            );
        }
    };

    /* ── Paywall Modal ─────────────────────────────────────────────────────── */
    /* ── Paywall Modal — VERSIÓN PREMIUM ──────────────────────────────────────── */
    const PaywallModal = () => {
        const isOverdue = paywallReason === "overdue";
        const isTimeout = paywallReason === "timeout";
        const isTokens = !isOverdue && !isTimeout;

        const CONFIG = {
            overdue: {
                emoji: "📅",
                glow: C.red,
                glowDim: C.redDim,
                badge: "PLAN VENCIDO",
                title: "Tu suscripción expiró",
                body: "Renueva hoy y recupera acceso instantáneo a tus clases, tutores y ejercicios con IA.",
                footer: "Tarda menos de 2 minutos · Sin contratos",
                btnLabel: "💳 Renovar ahora →",
                btnColor: C.red,
                btnText: "#fff",
            },
            tokens: {
                emoji: "⚡",
                glow: "var(--em-fire-warn)",
                glowDim: C.accentDim,
                badge: "SIN CRÉDITOS IA",
                title: "Tokens IA agotados",
                body: "Usaste todos tus créditos de IA este mes. Actualiza tu plan para seguir practicando sin límites.",
                footer: "Los tokens se renuevan el 1° de cada mes",
                btnLabel: "⚡ Actualizar plan →",
                btnColor: "var(--em-fire-warn)",
                btnText: "#000",
            },
            timeout: {
                emoji: "⏱️",
                glow: C.blue,
                glowDim: C.blueDim,
                badge: "CONEXIÓN LENTA",
                title: "La IA tardó demasiado",
                body: "No se consumió ningún token. Verifica tu conexión a internet e intenta de nuevo.",
                footer: "El bloque se regenerará al reintentar",
                btnLabel: "🔄 Reintentar",
                btnColor: C.blue,
                btnText: "#fff",
            },
        };

        const cfg = isOverdue ? CONFIG.overdue : isTimeout ? CONFIG.timeout : CONFIG.tokens;

        const handleBtn = () => {
            setShowPaywall(false);
            if (!isTimeout) onExit();
        };

        return (
            <div style={{
                position: "fixed", inset: 0, zIndex: 200,
                background: "rgba(0,0,0,0.80)",
                backdropFilter: "blur(10px)",
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: "20px",
            }}>
                {/* Card */}
                <div style={{
                    width: "100%", maxWidth: "360px",
                    background: "var(--bg-primary)",
                    borderRadius: "24px",
                    boxShadow: `
                    -12px 12px 28px var(--shadow-dark),
                    12px -12px 28px var(--shadow-light),
                    0 0 60px ${cfg.glowDim}
                `,
                    overflow: "hidden",
                    position: "relative",
                }}>

                    {/* Línea de color superior */}
                    <div style={{
                        height: "3px",
                        background: `linear-gradient(90deg, transparent, ${cfg.glow}, transparent)`,
                    }} />

                    {/* Fondo glow decorativo */}
                    <div style={{
                        position: "absolute", top: 0, left: "50%",
                        transform: "translateX(-50%)",
                        width: "200px", height: "200px",
                        borderRadius: "50%",
                        background: `radial-gradient(circle, ${cfg.glowDim} 0%, transparent 70%)`,
                        pointerEvents: "none",
                    }} />

                    <div style={{ padding: "28px 24px 24px", position: "relative" }}>

                        {/* Badge */}
                        <div style={{
                            display: "inline-flex", alignItems: "center", gap: "6px",
                            background: cfg.glowDim,
                            border: `1px solid ${alpha(cfg.glow, 27)}`,
                            borderRadius: "20px", padding: "3px 12px",
                            marginBottom: "20px",
                        }}>
                            <div style={{
                                width: "5px", height: "5px", borderRadius: "50%",
                                background: cfg.glow,
                                boxShadow: `0 0 6px ${cfg.glow}`,
                            }} />
                            <span style={{
                                fontSize: "9px", fontWeight: 800,
                                color: cfg.glow, letterSpacing: "1.5px",
                            }}>
                                {cfg.badge}
                            </span>
                        </div>

                        {/* Emoji grande */}
                        <div style={{
                            fontSize: "52px", textAlign: "center",
                            marginBottom: "14px",
                            filter: `drop-shadow(0 0 12px ${alpha(cfg.glow, 40)})`,
                        }}>
                            {cfg.emoji}
                        </div>

                        {/* Título */}
                        <h2 style={{
                            fontSize: "20px", fontWeight: 900,
                            color: "var(--text-primary)",
                            textAlign: "center", marginBottom: "10px",
                            letterSpacing: "-0.3px",
                        }}>
                            {cfg.title}
                        </h2>

                        {/* Cuerpo */}
                        <p style={{
                            fontSize: "13px", color: "var(--text-secondary)",
                            lineHeight: 1.7, textAlign: "center",
                            marginBottom: "22px",
                        }}>
                            {cfg.body}
                        </p>

                        {/* Separador */}
                        <div style={{
                            height: "1px",
                            background: C.veil06,
                            marginBottom: "18px",
                        }} />

                        {/* Botón principal */}
                        <button
                            onClick={handleBtn}
                            style={{
                                width: "100%", padding: "14px",
                                borderRadius: "14px", border: "none",
                                background: `linear-gradient(135deg, ${alpha(cfg.glow, 13)}, ${alpha(cfg.glow, 7)})`,
                                color: cfg.glow,
                                fontSize: "14px", fontWeight: 800,
                                cursor: "pointer", fontFamily: "inherit",
                                letterSpacing: "0.2px",
                                boxShadow: `
                                0 0 0 1px ${alpha(cfg.glow, 27)},
                                -4px 4px 10px var(--shadow-dark),
                                4px -4px 10px var(--shadow-light),
                                0 0 20px ${alpha(cfg.glow, 9)}
                            `,
                                marginBottom: "10px",
                                transition: "all 0.2s",
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.background = `linear-gradient(135deg, ${alpha(cfg.glow, 21)}, ${alpha(cfg.glow, 13)})`;
                                e.currentTarget.style.boxShadow = `0 0 0 1px ${alpha(cfg.glow, 53)}, -4px 4px 10px var(--shadow-dark), 4px -4px 10px var(--shadow-light), 0 0 28px ${alpha(cfg.glow, 19)}`;
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.background = `linear-gradient(135deg, ${alpha(cfg.glow, 13)}, ${alpha(cfg.glow, 7)})`;
                                e.currentTarget.style.boxShadow = `0 0 0 1px ${alpha(cfg.glow, 27)}, -4px 4px 10px var(--shadow-dark), 4px -4px 10px var(--shadow-light), 0 0 20px ${alpha(cfg.glow, 9)}`;
                            }}
                        >
                            {cfg.btnLabel}
                        </button>

                        {/* Footer note */}
                        <p style={{
                            fontSize: "10px", color: C.faint,
                            textAlign: "center", marginBottom: "12px",
                            letterSpacing: "0.3px",
                        }}>
                            {cfg.footer}
                        </p>

                        {/* Cerrar */}
                        <button onClick={() => setShowPaywall(false)} style={{
                            display: "block", width: "100%",
                            background: "transparent", border: "none",
                            color: C.faint, fontSize: "11px",
                            cursor: "pointer", fontFamily: "inherit",
                            fontWeight: 700, padding: "4px",
                        }}>
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    /* ══════════════════════════════════════════════════════════════════════
       RENDER
    ══════════════════════════════════════════════════════════════════════ */
    return (
        <div className="engine-mode">

            {/* ── Fondo: partículas animadas ────────────────────────────── */}
            <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0, overflow: "hidden" }}>
                {[
                    { top: "8%", left: "15%", color: C.accent, size: 3 },
                    { top: "12%", left: "72%", color: C.blue, size: 4 },
                    { top: "18%", left: "40%", color: C.green, size: 3 },
                    { top: "25%", left: "88%", color: C.purple, size: 5 },
                    { top: "30%", left: "28%", color: C.orange, size: 3 },
                    { top: "35%", left: "60%", color: C.accent, size: 4 },
                    { top: "42%", left: "8%", color: C.blue, size: 3 },
                    { top: "48%", left: "50%", color: C.green, size: 5 },
                    { top: "53%", left: "80%", color: C.accent, size: 3 },
                    { top: "58%", left: "35%", color: C.purple, size: 4 },
                    { top: "63%", left: "92%", color: C.orange, size: 3 },
                    { top: "68%", left: "18%", color: C.blue, size: 5 },
                    { top: "72%", left: "65%", color: C.accent, size: 3 },
                    { top: "78%", left: "45%", color: C.green, size: 4 },
                    { top: "83%", left: "78%", color: C.purple, size: 3 },
                    { top: "88%", left: "22%", color: C.orange, size: 5 },
                    { top: "92%", left: "55%", color: C.accent, size: 3 },
                    { top: "95%", left: "10%", color: C.blue, size: 4 },
                    { top: "55%", left: "70%", size: 35, color: C.accentDim, delay: "1.5s" },
                    { top: "75%", left: "20%", size: 45, color: "var(--em-particle-b)", delay: "3s" },
                    { top: "25%", left: "80%", size: 40, color: C.purpleDim, delay: "0.8s" },
                    { top: "85%", left: "85%", size: 35, color: C.orangeDim, delay: "2s" },
                    { top: "40%", left: "3%", size: 30, color: C.accentDim, delay: "4s" },
                    { top: "15%", left: "50%", size: 40, color: "var(--em-particle-b)", delay: "1s" },
                    { top: "65%", left: "45%", size: 50, color: "var(--em-particle-c)", delay: "2.5s" },
                ].map((p, i) => (
                    <div key={i} style={{
                        position: "absolute",
                        top: p.top, left: p.left,
                        width: `${p.size}px`, height: `${p.size}px`,
                        borderRadius: "50%",
                        background: p.color,
                        opacity: 0,
                        animation: `particleFade ${3.5 + (i % 5) * 0.7}s ease-in-out infinite`,
                        animationDelay: `${(i * 0.4) % 4}s`,
                        boxShadow: `0 0 ${p.size * 2}px ${alpha(p.color, 53)}`,
                    }} />
                ))}
            </div>

            {/* ── TOPBAR ────────────────────────────────────────────────────── */}
            <div className="em-topbar" style={{
                padding: "10px 18px", display: "flex", alignItems: "center", gap: "10px",
                background: "var(--bg-primary)",
                boxShadow: "0 4px 10px var(--shadow-dark), 0 2px 4px var(--shadow-light)"
            }}>
                <button onClick={onExit} style={{
                    background: "var(--bg-secondary)",
                    borderRadius: "9px", padding: "5px 12px",
                    cursor: "pointer", fontSize: "clamp(11px, 1.2vw, 14px)", color: C.muted, fontFamily: "inherit",
                    fontWeight: 700, display: "flex", alignItems: "center", gap: "5px",
                    border: "none",
                    boxShadow: "-3px 3px 6px var(--shadow-dark), 3px -3px 6px var(--shadow-light)"
                }}>
                    ← Salir
                </button>
                <div style={{ flex: 1, overflow: "hidden" }}>
                    <div className="em-topbar-title" style={{
                        fontSize: "clamp(12px, 1.4vw, 18px)", fontWeight: 800, color: C.text,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
                    }}>
                        {lessonTitle}
                    </div>
                    {/* Dice de qué modo viene el contenido —que sí le importa
                        al alumno, porque hay dos— y en qué bloque va. Nada más:
                        ni el proveedor del modelo ni el nombre interno del
                        motor le sirven para nada. */}
                    <div className="em-topbar-sub" style={{ fontSize: "clamp(9px, 1.1vw, 13px)", color: C.faint }}>
                        {blockData?.source === "curated"
                            ? T(nivelUI, "temario")
                            : T(nivelUI, "motorIA")}
                        {screen === "block" &&
                            ` · ${T(nivelUI, "bloque")} ${currentBlock + 1}/${motorInfo?.total_blocks || "?"}`}
                    </div>
                </div>
                {screen === "block" && (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <XPBar xp={xp} level={level} />
                        {tokenInfo && <TokenBadge remaining={tokenInfo.remaining} limit={tokenInfo.limit} />}
                    </div>
                )}
                {(screen === "overview" || screen === "block_result") && (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{
                            fontSize: "clamp(9px, 1.1vw, 13px)", color: C.green, fontWeight: 700,
                            background: C.greenDim, borderRadius: "10px", padding: "2px 8px",
                            boxShadow: "inset -2px 2px 4px var(--shadow-dark), inset 2px -2px 4px var(--shadow-light)"
                        }}>
                            {Object.keys(completedBlocks).length}/{motorInfo?.total_blocks || "?"} bloques
                        </span>
                        {tokenInfo && <TokenBadge remaining={tokenInfo.remaining} limit={tokenInfo.limit} />}
                    </div>
                )}
            </div>

            {/* ── CONTENT ───────────────────────────────────────────────────── */}
            <div className="em-scroll-area">

                {/* SETUP */}
                {screen === "setup" && motorInfo && (
                    <EngineSetup lessonTitle={lessonTitle} lessonLevel={lessonLevel} onLaunch={handleSetupDone} onGoClassic={onGoClassic} />
                )}
                {screen === "setup" && !motorInfo && (
                    <div style={{
                        display: "flex", alignItems: "center", justifyContent: "center",
                        minHeight: "70vh", flexDirection: "column", gap: "14px", position: "relative", zIndex: 1
                    }}>
                        <div style={{ fontSize: "42px", animation: "spinSlow 2s linear infinite" }}>⚡</div>
                        <p style={{ color: C.muted, fontSize: "13px" }}>Cargando motor...</p>
                    </div>
                )}

                {/* OVERVIEW */}
                {screen === "overview" && (
                    <BlockOverview
                        nivel={nivelUI}
                        motorInfo={motorInfo}
                        completedBlocks={completedBlocks}
                        lessonTitle={lessonTitle}
                        onStartBlock={startBlock}
                        onBack={() => setScreen("setup")}
                        spacyDone={spacyDone}
                        onStartSpacy={startSpacy}
                        hasSpacy={hasSpacy}
                    />
                )}

                {/* Spacy NO se renderiza aquí dentro. Ver el comentario de la
                    cabecera del archivo: el contenedor `.engine-mode` impone
                    `overflow-x: hidden` y `button { white-space: nowrap }`, que
                    le romperían el modo cinema y los botones. Se pide hacia
                    arriba y LessonPage lo monta en el mismo sitio donde vivía. */}

                {/* LOADING */}
                {screen === "loading" && (
                    <EngineLoader
                        lesson={{ title: motorInfo?.blocks?.[currentBlock]?.label || "Generando bloque..." }}
                        config={sessionConfig}
                        isDone={isLoadingDone}
                        onComplete={handleLoaderComplete}
                    />
                )}

                {/* BLOCK GAME */}
                {screen === "block" && blockData && (
                    <div className="em-content-wrapper" style={{
                        padding: "18px 18px 40px", maxWidth: "clamp(520px, 60vw, 720px)", margin: "0 auto",
                        position: "relative", zIndex: 1
                    }}>

                        {/* Block banner */}
                        <div style={{
                            display: "flex", alignItems: "center", gap: "8px",
                            padding: "8px 12px", marginBottom: "16px",
                            ...insetNeu({ borderRadius: "11px" })
                        }}>
                            <span style={{ fontSize: "clamp(14px, 1.6vw, 20px)" }}>{BLOCK_ICONS[currentBlock] || "📌"}</span>
                            <span style={{
                                fontSize: "clamp(11px, 1.4vw, 16px)", color: C.accent, fontWeight: 700, flex: 1,
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
                            }}>
                                Bloque {currentBlock + 1}: {blockData.block_label}
                            </span>
                            <span style={{ fontSize: "clamp(10px, 1.2vw, 14px)", color: C.faint, flexShrink: 0 }}>
                                {faseVisible === "grammar" ? "📖 Gram." : "✏️"}{" "}
                                {faseVisible === "exercises" ? `${exerciseIdx + 1}/${totalExercises}` : ""}
                            </span>
                        </div>

                        {/* ── De dónde salió este bloque ─────────────────────────
                            Cuando se acaban los tokens el bloque se sirve del
                            temario en vez de generarse. El alumno sigue
                            avanzando, pero se le dice — no se le hace creer que
                            sigue teniendo contenido personalizado. */}
                        {blockData.source === "curated" && (
                            <div style={{
                                display: "flex", alignItems: "center", gap: "8px",
                                padding: "9px 12px", marginBottom: "16px",
                                ...insetNeu({ borderRadius: "11px" })
                            }}>
                                <span style={{ fontSize: "14px" }}>📚</span>
                                <span style={{ fontSize: "11px", color: C.muted, lineHeight: 1.5 }}>
                                    Sin tokens IA: estos ejercicios vienen del temario NSL, no
                                    generados para ti. <strong style={{ color: C.text }}>Tu avance
                                    cuenta igual.</strong>
                                </span>
                            </div>
                        )}

                        {/* HUD */}
                        {faseVisible === "exercises" && !esArena && (
                            <>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px" }}>
                                    <FireBar energy={fireEnergy} shaking={fireShake} />
                                    {streak >= 2 && (
                                        <div style={{ fontSize: "11px", fontWeight: 800, color: C.orange, whiteSpace: "nowrap" }}>
                                            🔥 {streak}×
                                        </div>
                                    )}
                                </div>
                                <div style={{ marginBottom: "16px" }}>
                                    <ExerciseProgressDots
                                        total={totalExercises}
                                        current={exerciseIdx}
                                        done={totalAnswered - (totalExercises - exerciseIdx - 1)}
                                    />
                                </div>
                            </>
                        )}

                        {/* Exercise type label */}
                        {faseVisible === "exercises" && currentExercise && !esArena && (
                            <div style={{
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                marginBottom: "12px"
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <span style={{
                                        fontSize: "9px", background: C.accentDim, color: C.accent,
                                        borderRadius: "20px", padding: "3px 10px", fontWeight: 700,
                                        letterSpacing: "1.5px", textTransform: "uppercase",
                                        boxShadow: "-2px 2px 4px var(--shadow-dark), 2px -2px 4px var(--shadow-light)"
                                    }}>
                                        {currentExercise.type?.replace("_", " ").toUpperCase()}
                                    </span>
                                </div>
                                <span style={{ fontSize: "10px", color: C.faint, fontWeight: 700 }}>
                                    {blockScore}/{totalExercises * 2} pts
                                </span>
                            </div>
                        )}

                        {/* Card — Neumorphic */}
                        <div key={animKey} className="em-card-in"
                            style={esArena
                                // La Arena trae su propio fondo —el abismo
                                // índigo— y el neumorfismo claro de alrededor lo
                                // encajonaría dentro de la lección. Aquí el
                                // contenedor desaparece.
                                ? { borderRadius: "20px" }
                                : { ...cardNeu({ borderRadius: "20px" }), padding: "24px 20px" }}>
                            {faseVisible === "grammar" ? (
                                <EngineGrammarCard
                                    grammarNote={blockData.grammar_note}
                                    blockLabel={blockData.block_label}
                                    onNext={() => { setExercisePhase("exercises"); setAnimKey(k => k + 1); }}
                                />
                            ) : renderExerciseCard(currentExercise)}
                        </div>

                        {/* En la Arena no hay "ejercicio 1 de 1" ni botón de
                            regenerar: no es un ejercicio y no hay nada que
                            regenerar — el personaje lo decide la Arena al abrir
                            el micrófono, y pedirle a la IA del motor que genere
                            un `arena_mission` devolvería un error. */}
                        {faseVisible === "exercises" && !esArena && (
                            <div style={{ textAlign: "center", marginTop: "12px" }}>
                                <p style={{ fontSize: "10px", color: C.faint, marginBottom: "16px" }}>
                                    {T(nivelUI, "ejercicio")} {exerciseIdx + 1} {T(nivelUI, "de")} {totalExercises}
                                </p>

                                {/* Regenerar cuesta una llamada a la IA. En un bloque
                                    curado no hay tokens que gastar, así que el botón
                                    no se ofrece: prometería algo que devolvería 429. */}
                                {blockData.source !== "curated" && (
                                <button
                                    onClick={handleRegenerateExercise}
                                    disabled={isRegenerating}
                                    style={{
                                        background: C.veil03,
                                        border: "1px solid rgba(255,255,255,0.08)",
                                        borderRadius: "20px",
                                        padding: "8px 16px",
                                        color: C.faint,
                                        fontSize: "11px",
                                        cursor: isRegenerating ? "wait" : "pointer",
                                        transition: "all 0.25s ease",
                                        opacity: isRegenerating ? 0.6 : 1,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px"
                                    }}
                                    onMouseEnter={e => {
                                        if (!isRegenerating) {
                                            e.currentTarget.style.background = "rgba(255,140,66,0.1)";
                                            e.currentTarget.style.borderColor = "rgba(255,140,66,0.3)";
                                            e.currentTarget.style.color = C.orange;
                                        }
                                    }}
                                    onMouseLeave={e => {
                                        if (!isRegenerating) {
                                            e.currentTarget.style.background = C.veil03;
                                            e.currentTarget.style.borderColor = C.veil08;
                                            e.currentTarget.style.color = C.faint;
                                        }
                                    }}
                                >
                                    {isRegenerating ? T(nivelUI, "regenerando") : T(nivelUI, "regenerar")}
                                </button>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* BLOCK RESULT */}
                {screen === "block_result" && blockData && (
                    <BlockResult
                        nivel={nivelUI}
                        blockLabel={blockData.block_label}
                        score={scoreGuardado ?? blockScore}
                        record={recordBloque}
                        sinNota={(blockData.exercises || []).some(e => e?.type === "arena_mission")}
                        maxScore={totalExercises * 2 || 10}
                        isLastBlock={blockData.is_last_block}
                        onNextBlock={() => { fxAvanzar(); startBlock(currentBlock + 1); }}
                        onOverview={() => { fxAvanzar(); setScreen("overview"); }}
                    />
                )}
            </div>

            {/* ── Paywall Modal ─────────────────────────────────────────────── */}
            {showPaywall && <PaywallModal />}
        </div>
    );
}
